// Checkout público do Control Center — visual e fluxo portados 1:1 do
// checkout do Reservei_Web (`src/pages/app/Checkout.tsx`), por pedido
// explícito do dono ("pode copiar exatamente igual").
//
// Única diferença vs o original: integração. Aqui o comprador NÃO está
// logado — nome/e-mail entram no formulário (pré-preenchidos via query
// params), CPF/telefone vêm do form do cartão ou do BillingInfoModal (PIX),
// e o backend é a API pública do CC (`/api/public/checkout`).
//
// Rota: /checkout/:slug?saas=X&cycle=anual&email=&name=&redirect=URL
//
// Página white-label — tokens visuais (teal/slate/cream do Reservei) são
// locais por design; exceção documentada ao DESIGN_SYSTEM.md do CC.

import { useQuery } from '@tanstack/react-query';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion, useMotionValue, useSpring } from 'framer-motion';
import type { Variants } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Copy,
  CreditCard,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isValidCpfOrCnpj, normalizeDocument } from '@control-center/shared';

import { maskCpfCnpjAuto } from '../../lib/masks';

import { cn } from '@control-center/ui';

import { mapAsaasError } from '../../lib/asaas-errors';
import {
  fetchPaymentDetails,
  fetchPaymentStatus,
  fetchPublicPlan,
  submitPublicCheckout,
  validateCoupon,
  clearReserveiCheckoutIntent,
} from '../../lib/public-checkout-api';
import type { CouponValidationResult, PublicCheckoutPayload } from '../../lib/public-checkout-api';
import { useAnimatedNumber } from '../../lib/useAnimatedNumber';
import { AsaasIcon, EloIcon, MasterCardIcon, PixIcon, VisaIcon } from './branded-icons';
import { BillingInfoModal } from './BillingInfoModal';
import { CHECKOUT_TERMS_VERSION } from './terms';
import { resolveSuccessTarget, useCheckoutEntry, type CheckoutBillingCycle } from './use-checkout-entry';

type CheckoutStep =
  | 'method' // escolher método de pagamento
  | 'payment' // form do cartão
  | 'pix' // QR Code PIX
  | 'review'
  | 'processing'
  | 'success';
type PaymentMethod = 'CREDIT_CARD' | 'PIX';

interface PixPaymentData {
  chargeId: string;
  amount: number;
  pixQrCodeUrl: string | null;
  pixCopiaECola: string | null;
  expiresAt: string | null;
}

const TERMS_VERSION = CHECKOUT_TERMS_VERSION;

// R1 + R5 — aceite obrigatório dos Termos/Política + aviso de cobrança recorrente.
// Renderizado antes do botão Pagar (comum aos layouts desktop e mobile).
// NOTA: os links apontam pras rotas /termos-de-uso e /politica-de-privacidade,
// que precisam ser publicadas com o texto jurídico revisado (R1c).
function ConsentBlock({
  checked,
  onChange,
  priceLabel,
  oneTime = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  priceLabel: string;
  /** Serviço de pagamento único: não há renovação nem o que cancelar depois. */
  oneTime?: boolean;
}) {
  return (
    <div className="mt-4 space-y-2">
      {oneTime ? (
        <p className="text-[12px] leading-snug text-slate-500">
          Cobrança <span className="font-medium text-slate-600">única</span> de {priceLabel}, sem
          renovação.
        </p>
      ) : (
        <p className="text-[12px] leading-snug text-slate-500">
          A assinatura <span className="font-medium text-slate-600">renova automaticamente</span> (
          {priceLabel}) até você cancelar. Você pode cancelar quando quiser.
        </p>
      )}
      <label className="flex items-start gap-2 cursor-pointer text-[12px] leading-snug text-slate-600">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-[2px] h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-400 cursor-pointer"
        />
        <span>
          Li e aceito os{' '}
          <a
            href="/termos-de-uso"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
          >
            Termos de Uso
          </a>{' '}
          e a{' '}
          <a
            href="/politica-de-privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900"
          >
            Política de Privacidade
          </a>
          .
        </span>
      </label>
    </div>
  );
}

// Helpers para mascarar inputs do cartão
const formatCardNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
};

const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 2) {
    return digits.slice(0, 2) + '/' + digits.slice(2);
  }
  return digits;
};

const formatCVV = (value: string): string => {
  return value.replace(/\D/g, '').slice(0, 4);
};

const getMaskedCardNumber = (cardNumber: string): string => {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 4) return '';
  return '•••• ' + digits.slice(-4);
};

const formatPhone = (value: string): string => {
  let d = value.replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  const digits = d.slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const formatPostalCode = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatMoney = (n: number): string =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Substitui a utility `.glow-on-focus` do Reservei (index.css)
const glowFocus =
  'focus-visible:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)]';

// =====================================================
// ANIMATION VARIANTS — sistema coeso
// Easing: cubic-bezier expo-out (Linear/Vercel signature)
// =====================================================
const EXPO_OUT = [0.16, 1, 0.3, 1] as const;

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: EXPO_OUT },
  },
};

const sidebarVariants: Variants = {
  hidden: { opacity: 0, x: 30 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.9, ease: EXPO_OUT, delay: 0.4 },
  },
};

/**
 * `crypto.randomUUID` não existe em navegador antigo (Safari < 15.4) nem fora de
 * https — chamar direto quebraria a tela inteira. Sem ela a compra segue: o
 * servidor gera a chave (perde só a proteção contra clique duplo).
 */
function newCheckoutKey(): string | undefined {
  return typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : undefined;
}

export function CheckoutFlow(): JSX.Element {
  const navigate = useNavigate();
  const { slug, saasOrigin, handoffToken, redirectUrl, initialCycle, initialName, initialEmail } =
    useCheckoutEntry();
  const planQuery = useQuery({
    queryKey: ['public-plan', slug, saasOrigin],
    queryFn: () => fetchPublicPlan(slug as string, saasOrigin),
    enabled: Boolean(slug),
    retry: false,
  });
  const plan = planQuery.data ?? null;
  // Uma chave por visita: clique duplo ou reenvio cai na MESMA tentativa no
  // servidor, que devolve a cobrança já criada em vez de cobrar de novo.
  const checkoutKeyRef = useRef<string | undefined>(undefined);
  if (checkoutKeyRef.current === undefined) checkoutKeyRef.current = newCheckoutKey();

  // Estados
  const [step, setStep] = useState<CheckoutStep>('method');
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // R1/LGPD — aceite obrigatório dos Termos + Política antes de pagar.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentData, setPaymentData] = useState<PixPaymentData | null>(null);

  // Ciclo de cobrança — inicial vem de ?cycle= ('anual' | 'YEARLY' → anual)
  const [cycleChoice, setCycleChoice] = useState<CheckoutBillingCycle>(initialCycle);

  // Dados do comprador (no original vinham do useAuth — aqui é público)
  const [buyerName, setBuyerName] = useState(initialName);
  const [buyerEmail, setBuyerEmail] = useState(initialEmail);
  const [buyerDoc, setBuyerDoc] = useState(''); // só dígitos — vem do BillingInfoModal (PIX)
  const [buyerPhone, setBuyerPhone] = useState(''); // só dígitos

  // Cupom de desconto (1ª cobrança). `couponResult` é só pra UI — o backend
  // re-valida e recobra no submit (nunca confiar no valor do front).
  const [couponInput, setCouponInput] = useState('');
  const [couponResult, setCouponResult] = useState<CouponValidationResult | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Estados para PIX
  const [pixLoading, setPixLoading] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [pixExpired, setPixExpired] = useState(false);
  const [pixSecondsLeft, setPixSecondsLeft] = useState(0);

  // Estado do modal de billing info (CPF/CNPJ)
  const [billingInfoModalOpen, setBillingInfoModalOpen] = useState(false);
  // Guarda qual ação retomar quando user salvar os dados
  const pendingActionAfterBillingRef = useRef<((doc: string, phone: string) => void) | null>(null);

  // Magnetic CTA — cursor tracking sutil (somente em devices com hover real)
  const ctaX = useMotionValue(0);
  const ctaY = useMotionValue(0);
  const ctaSpringX = useSpring(ctaX, { stiffness: 200, damping: 20, mass: 0.5 });
  const ctaSpringY = useSpring(ctaY, { stiffness: 200, damping: 20, mass: 0.5 });
  // Detecta hover capability — touch devices não disparam o efeito
  const hasHoverRef = useRef<boolean>(true);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      hasHoverRef.current = window.matchMedia('(hover: hover)').matches;
    }
  }, []);
  const handleCtaMouseMove = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (!hasHoverRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    ctaX.set(x * 0.15);
    ctaY.set(y * 0.3);
  };
  const handleCtaMouseLeave = (): void => {
    if (!hasHoverRef.current) return;
    ctaX.set(0);
    ctaY.set(0);
  };

  const PIX_EXPIRATION_SECONDS = 5 * 60; // fallback se backend não mandar pix_expiration
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expirationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const confettiTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pixCopiedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const aliveRef = useRef(true);

  // Estados do cartão de crédito
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCVV, setCardCVV] = useState('');

  // Dados adicionais do titular (obrigatório no Brasil)
  const [holderCPF, setHolderCPF] = useState('');
  const [holderPhone, setHolderPhone] = useState('');
  const [holderPostalCode, setHolderPostalCode] = useState('');
  const [holderAddressNumber, setHolderAddressNumber] = useState('');

  // Fontes da página (Open Sans + Playfair Display) — só nessa rota white-label
  useEffect(() => {
    const id = 'cc-checkout-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@1,700;1,900&display=swap';
    document.head.appendChild(link);
  }, []);

  // Preço — vem do plano público do CC
  // Serviço de pagamento único (implantação — peça 4): cobra a taxa única uma
  // vez, sem seletor de ciclo e sem "renovação". O servidor recusa qualquer
  // outro ciclo pra esse plano, então a tela manda sempre `pagamento_unico`.
  const isOneTime = plan?.billing_cycle === 'pagamento_unico';
  const setupFee = plan ? Number.parseFloat(plan.setup_fee ?? '0') : 0;
  const monthlyPrice = plan ? Number.parseFloat(plan.monthly_price) : 0;
  const annualPriceRaw = plan?.annual_price != null ? Number.parseFloat(plan.annual_price) : null;
  const hasAnnual =
    !isOneTime && annualPriceRaw != null && Number.isFinite(annualPriceRaw) && annualPriceRaw > 0;
  const billingCycle: CheckoutBillingCycle =
    hasAnnual && cycleChoice === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
  const planPrice = isOneTime
    ? setupFee
    : billingCycle === 'YEARLY' && annualPriceRaw != null
      ? annualPriceRaw
      : monthlyPrice;
  const cycleParam: 'mensal' | 'anual' | 'pagamento_unico' = isOneTime
    ? 'pagamento_unico'
    : billingCycle === 'YEARLY'
      ? 'anual'
      : 'mensal';
  const cycleTitle = isOneTime
    ? 'Pagamento único'
    : billingCycle === 'YEARLY'
      ? 'Assinatura Anual'
      : 'Assinatura Mensal';
  const cycleTitleLower = isOneTime
    ? 'Pagamento único'
    : billingCycle === 'YEARLY'
      ? 'Assinatura anual'
      : 'Assinatura mensal';
  const cycleSuffix = isOneTime ? '' : billingCycle === 'YEARLY' ? '/ano' : '/mês';
  const cycleShort = isOneTime ? 'Único' : billingCycle === 'YEARLY' ? 'Anual' : 'Mensal';
  const planName = plan?.name ?? '';
  const saasName = plan?.saas?.name ?? null;
  const homeUrl = plan?.saas?.url ?? null;

  // Lista de destaques do plano. Prioridade: o que o admin configurou no
  // plano (Plan.checkoutHighlights, via API pública). Sem configuração,
  // fallback genérico montado de description + trial (comportamento original).
  const customHighlights = (plan?.highlights ?? []).filter((h) => h.trim().length > 0);
  const planHighlights: string[] =
    customHighlights.length > 0
      ? customHighlights
      : [
          ...(plan?.description ? [plan.description] : []),
          plan && plan.trial_days > 0
            ? `${plan.trial_days} dias grátis pra testar`
            : 'Ativação imediata após pagamento',
          isOneTime ? 'Cobrança única, sem renovação' : 'Cancele a qualquer momento',
          'Suporte incluso no plano',
        ];

  // Cupom: `effectivePrice` é o que se paga AGORA (1ª cobrança com desconto);
  // `planPrice` segue sendo o preço cheio (subtotais e taxa recorrente).
  const couponApplied = couponResult?.valid === true ? couponResult : null;
  const couponDiscount = couponApplied ? couponApplied.discount_amount : 0;
  const effectivePrice = couponApplied ? couponApplied.final_price : planPrice;

  // Counter animado pro Total — anima o valor efetivo (com cupom)
  const animatedTotal = useAnimatedNumber(effectivePrice, 1200);

  // Trocar de ciclo muda o preço base → o desconto vigente fica inválido.
  // Limpa o cupom aplicado pra não mostrar desconto velho num preço novo.
  useEffect(() => {
    setCouponResult(null);
  }, [billingCycle]);

  const applyCoupon = async (): Promise<void> => {
    if (!plan || couponLoading) return;
    const code = couponInput.trim();
    if (!code) return;
    setCouponLoading(true);
    try {
      const res = await validateCoupon({
        code,
        plan_slug: plan.slug,
        saas_origin: saasOrigin,
        billing_cycle: cycleParam,
        email: buyerEmail.trim() || undefined,
      });
      setCouponResult(res);
      if (res.valid) toast.success('Cupom aplicado!');
      else toast.error(res.message);
    } catch {
      setCouponResult(null);
      toast.error('Não foi possível validar o cupom. Tente novamente.');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = (): void => {
    setCouponResult(null);
    setCouponInput('');
  };

  // Confetti no sucesso
  const triggerConfetti = useCallback(() => {
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#000', '#333', '#666'],
      zIndex: 9999,
    });
  }, []);

  // Limpar polling e timers ao desmontar
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
      if (pixCopiedTimerRef.current) clearTimeout(pixCopiedTimerRef.current);
      if (expirationTimerRef.current) clearInterval(expirationTimerRef.current);
    };
  }, []);

  const goSuccess = (): void => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (expirationTimerRef.current) clearInterval(expirationTimerRef.current);
    setStep('success');
    if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
    confettiTimerRef.current = setTimeout(triggerConfetti, 300);
  };

  // Validação dos dados do comprador (substitui o useAuth do original)
  const validateBuyer = (): boolean => {
    if (buyerName.trim().length < 3) {
      toast.error('Preencha seu nome completo');
      return false;
    }
    if (!EMAIL_RE.test(buyerEmail.trim())) {
      toast.error('Preencha um e-mail válido');
      return false;
    }
    return true;
  };

  // Validar dados do cartão
  const isCardValid = (): boolean => {
    const digits = cardNumber.replace(/\D/g, '');
    const expiryParts = cardExpiry.split('/');
    // CNPJ alfanumérico (DR-012): normaliza [0-9A-Z], aceita PF (11) e PJ (14)
    const cpfDigits = normalizeDocument(holderCPF);
    const phoneDigits = holderPhone.replace(/\D/g, '');
    const postalDigits = holderPostalCode.replace(/\D/g, '');

    return (
      cardName.trim().length >= 3 &&
      digits.length >= 15 &&
      expiryParts.length === 2 &&
      expiryParts[0]?.length === 2 &&
      expiryParts[1]?.length === 2 &&
      cardCVV.length >= 3 &&
      (cpfDigits.length === 11 || cpfDigits.length === 14) &&
      phoneDigits.length >= 10 &&
      postalDigits.length === 8 &&
      holderAddressNumber.trim().length >= 1
    );
  };

  // Ir para tela de revisão
  const goToReview = (): void => {
    if (!plan) return;

    // Validar dados do cartão
    if (!isCardValid()) {
      toast.error('Preencha todos os dados do cartão corretamente');
      return;
    }
    if (!isValidCpfOrCnpj(normalizeDocument(holderCPF))) {
      toast.error('CPF/CNPJ inválido. Verifique os dígitos.');
      return;
    }

    setStep('review');
  };

  // Aguarda confirmação do cartão (polling ~60s) — substitui o
  // waitForSubscriptionActivation do original (aqui o status é da cobrança)
  const waitForCardConfirmation = async (chargeId: string): Promise<boolean> => {
    const POLL_INTERVAL_MS = 4000;
    const MAX_ATTEMPTS = 15; // ~60s
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      if (!aliveRef.current) return false;
      try {
        const status = await fetchPaymentStatus(chargeId);
        if (status.paid) return true;
        // Cartão recusado chega como status terminal não-pago (ChargeStatus do CC)
        if (status.status === 'failed' || status.status === 'cancelada') {
          throw new Error('Pagamento não autorizado pela operadora.');
        }
      } catch (err) {
        // Erro transitório de rede — re-tenta no próximo ciclo
        if (err instanceof Error && /não autorizado/i.test(err.message)) throw err;
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    return false;
  };

  // Handler para processar pagamento com cartão de crédito
  const handlePayment = async (): Promise<void> => {
    if (!plan || isProcessing) return;

    setIsProcessing(true);
    setStep('processing');

    try {
      const expiryParts = cardExpiry.split('/');
      const expiryYear =
        expiryParts[1]?.length === 2 ? '20' + expiryParts[1] : (expiryParts[1] ?? '');

      const payload: PublicCheckoutPayload = {
        plan_slug: plan.slug,
        email: buyerEmail.trim(),
        name: buyerName.trim(),
        cpf_cnpj: normalizeDocument(holderCPF),
        phone: holderPhone.replace(/\D/g, '') || undefined,
        payment_method: 'credit_card',
        saas_origin: saasOrigin,
        handoff_token: handoffToken,
        checkout_key: checkoutKeyRef.current,
        billing_cycle: cycleParam,
        coupon_code: couponApplied ? couponApplied.code : undefined,
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        credit_card: {
          number: cardNumber.replace(/\s/g, ''),
          holder_name: cardName.trim(),
          expiry_month: expiryParts[0] ?? '',
          expiry_year: expiryYear,
          ccv: cardCVV,
          postal_code: holderPostalCode.replace(/\D/g, ''),
          address_number: holderAddressNumber.trim(),
        },
      };

      const result = await submitPublicCheckout(payload);
      if (handoffToken) clearReserveiCheckoutIntent();

      // Trial: sem cobrança agora — sucesso direto
      if (result.is_trial || result.status === 'em_teste') {
        goSuccess();
        return;
      }

      if (result.charge_id) {
        const paid = await waitForCardConfirmation(result.charge_id);
        if (!aliveRef.current) return;
        if (paid) {
          goSuccess();
          return;
        }
        // Confirmação demorou mais que ~60s — segue o acompanhamento na
        // página de pagamento (continua o polling lá)
        const params = new URLSearchParams();
        if (redirectUrl) params.set('redirect', redirectUrl);
        const qs = params.toString();
        window.location.href = `/checkout/pay/${result.charge_id}${qs ? `?${qs}` : ''}`;
        return;
      }

      // Sem cobrança retornada — backend ativou direto
      goSuccess();
    } catch (err) {
      if (!aliveRef.current) return;
      toast.error(mapAsaasError(err));
      setStep('review');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler para selecionar método de pagamento
  const handleSelectMethod = (method: PaymentMethod): void => {
    if (!validateBuyer()) return;
    if (method === 'CREDIT_CARD') {
      // Pré-preenche o form do titular com o que já temos (modal/PIX anterior)
      if (!holderCPF && (buyerDoc.length === 11 || buyerDoc.length === 14)) {
        setHolderCPF(maskCpfCnpjAuto(buyerDoc));
      }
      if (!holderPhone && buyerPhone) setHolderPhone(formatPhone(buyerPhone));
      setStep('payment');
    } else if (method === 'PIX') {
      // PIX precisa de CPF/CNPJ antes de criar a cobrança — mesmo papel do
      // BILLING_INFO_REQUIRED + modal do original, só que proativo
      if (isValidCpfOrCnpj(buyerDoc)) {
        void handlePixPayment();
      } else {
        pendingActionAfterBillingRef.current = (doc, phone) => {
          void handlePixPayment(doc, phone);
        };
        setBillingInfoModalOpen(true);
      }
    }
  };

  /**
   * Inicia countdown de expiração do PIX usando timestamp server-authoritative.
   * Só roda quando o backend manda `pix_expiration` — sem ele, o QR não tem
   * prazo curto e não mostramos countdown falso.
   */
  const startPixExpirationTimer = (expiresAt?: string): void => {
    if (expirationTimerRef.current) clearInterval(expirationTimerRef.current);
    setPixExpired(false);

    // Calcula expiresAtMs uma vez (server-authoritative)
    const expiresAtMs = expiresAt
      ? new Date(expiresAt).getTime()
      : Date.now() + PIX_EXPIRATION_SECONDS * 1000;

    const computeSecondsLeft = (): number => {
      return Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
    };

    // Set inicial — se já expirou, marca direto
    const initial = computeSecondsLeft();
    setPixSecondsLeft(initial);
    if (initial === 0) {
      setPixExpired(true);
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }

    expirationTimerRef.current = setInterval(() => {
      const left = computeSecondsLeft();
      setPixSecondsLeft(left);

      if (left <= 0) {
        // Expirou — para tudo
        if (expirationTimerRef.current) clearInterval(expirationTimerRef.current);
        if (pollingRef.current) clearInterval(pollingRef.current);
        setPixExpired(true);
      }
    }, 1000);
  };

  /**
   * Callback chamado quando user salva CPF no modal — retoma a ação pendente
   */
  const handleBillingInfoSaved = (doc: string, phone: string): void => {
    setBuyerDoc(doc);
    setBuyerPhone(phone);
    const action = pendingActionAfterBillingRef.current;
    pendingActionAfterBillingRef.current = null;
    if (action) action(doc, phone);
  };

  // Handler para criar pagamento PIX
  const handlePixPayment = async (docOverride?: string, phoneOverride?: string): Promise<void> => {
    if (!plan || pixLoading) return;
    const doc = docOverride ?? buyerDoc;
    const phoneDigits = phoneOverride ?? buyerPhone;

    setPixLoading(true);
    setStep('pix');
    setPixExpired(false);
    setPixSecondsLeft(0);
    setPaymentData(null);

    try {
      const payload: PublicCheckoutPayload = {
        plan_slug: plan.slug,
        email: buyerEmail.trim(),
        name: buyerName.trim(),
        cpf_cnpj: doc,
        phone: phoneDigits || undefined,
        payment_method: 'pix',
        saas_origin: saasOrigin,
        handoff_token: handoffToken,
        checkout_key: checkoutKeyRef.current,
        billing_cycle: cycleParam,
        coupon_code: couponApplied ? couponApplied.code : undefined,
        terms_accepted: true,
        terms_version: TERMS_VERSION,
      };
      const result = await submitPublicCheckout(payload);
      if (handoffToken) clearReserveiCheckoutIntent();

      // Trial: sem cobrança agora — sucesso direto
      if (result.is_trial || result.status === 'em_teste') {
        goSuccess();
        return;
      }

      if (!result.charge_id) {
        // Fallback raro — sem charge_id, manda pro checkout hospedado se houver
        if (result.payment_url) {
          window.location.href = result.payment_url;
          return;
        }
        goSuccess();
        return;
      }

      const details = await fetchPaymentDetails(result.charge_id);
      if (!aliveRef.current) return;
      const amount = Number.parseFloat(details.amount);
      setPaymentData({
        chargeId: result.charge_id,
        amount: Number.isFinite(amount) ? amount : planPrice,
        pixQrCodeUrl: details.pix_qr_image
          ? details.pix_qr_image.startsWith('data:')
            ? details.pix_qr_image
            : `data:image/png;base64,${details.pix_qr_image}`
          : null,
        pixCopiaECola: details.pix_payload,
        expiresAt: details.pix_expiration,
      });

      // Iniciar polling e timer de expiração (server-authoritative)
      startPixPolling(result.charge_id);
      if (details.pix_expiration) startPixExpirationTimer(details.pix_expiration);
    } catch (err) {
      if (!aliveRef.current) return;
      toast.error(mapAsaasError(err));
      setStep('method');
    } finally {
      setPixLoading(false);
    }
  };

  // Regenerar PIX (cria nova cobrança — o backend reaproveita/auto-vincula)
  const handleRegeneratePix = (): void => {
    if (!plan || pixLoading) return;
    void handlePixPayment();
  };

  // Polling para verificar status do PIX
  const startPixPolling = (chargeId: string): void => {
    // Limpar polling anterior se existir
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    // Verificar a cada 4 segundos por até 10 minutos.
    const POLL_INTERVAL_MS = 4000;
    let attempts = 0;
    const maxAttempts = 150; // 10 min / 4s
    let consecutiveErrors = 0;
    let inFlight = false;

    pollingRef.current = setInterval(() => {
      if (inFlight) return;
      attempts++;
      inFlight = true;

      fetchPaymentStatus(chargeId)
        .then((status) => {
          consecutiveErrors = 0; // reset em sucesso
          if (status.paid) {
            // Pagamento confirmado!
            goSuccess();
            return;
          }
          // Estado terminal NÃO-pago (cancelada/expirada no gateway) —
          // mesmo papel do `terminal/exists` do original: para o polling
          // e mostra a tela de expirado com CTA de gerar novo código
          if (
            status.status === 'cancelada' ||
            status.status === 'failed' ||
            status.status === 'vencida'
          ) {
            if (pollingRef.current) clearInterval(pollingRef.current);
            if (expirationTimerRef.current) clearInterval(expirationTimerRef.current);
            setPixExpired(true);
          }
        })
        .catch(() => {
          consecutiveErrors++;
          // Após 5 falhas consecutivas, para de martelar o backend
          if (consecutiveErrors >= 5 && pollingRef.current) {
            clearInterval(pollingRef.current);
            toast.error('Não conseguimos verificar o pagamento agora. Recarregue após pagar.');
          }
        })
        .finally(() => {
          inFlight = false;
          if (attempts >= maxAttempts && pollingRef.current) {
            clearInterval(pollingRef.current);
            toast.error('Tempo limite atingido. Verifique se o pagamento foi realizado.');
          }
        });
    }, POLL_INTERVAL_MS);
  };

  // Copiar código PIX
  const handleCopyPix = async (): Promise<void> => {
    if (!paymentData?.pixCopiaECola) return;

    try {
      await navigator.clipboard.writeText(paymentData.pixCopiaECola);
      setPixCopied(true);
      toast.success('Código PIX copiado!');

      if (pixCopiedTimerRef.current) clearTimeout(pixCopiedTimerRef.current);
      pixCopiedTimerRef.current = setTimeout(() => setPixCopied(false), 3000);
    } catch {
      toast.error('Erro ao copiar código');
    }
  };

  // Campo de cupom — reutilizado nos resumos (desktop + mobile).
  const renderCouponField = (): JSX.Element => (
    <div className="py-4 border-b border-black/[0.08]">
      {couponApplied ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="px-2 py-1 bg-teal-50 text-teal-700 text-[11px] font-bold uppercase tracking-wider rounded-[6px] truncate">
              {couponApplied.code}
            </span>
            <span className="text-[12px] text-teal-700 font-medium">aplicado</span>
          </div>
          <button
            type="button"
            onClick={removeCoupon}
            className={cn(
              'text-[12px] text-black/40 hover:text-black/70 underline underline-offset-2 cursor-pointer rounded-[6px] px-1',
              glowFocus,
            )}
          >
            remover
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void applyCoupon();
              }
            }}
            placeholder="Tem um cupom?"
            aria-label="Código do cupom de desconto"
            maxLength={50}
            className={cn(
              'flex-1 h-[44px] px-3 bg-white border border-black/[0.15] rounded-[10px] text-[15px] text-black placeholder:text-black/30 uppercase shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
              glowFocus,
            )}
          />
          <button
            type="button"
            onClick={() => void applyCoupon()}
            disabled={couponLoading || !couponInput.trim()}
            aria-busy={couponLoading}
            className={cn(
              'h-[44px] px-4 rounded-[10px] text-[13px] font-bold uppercase tracking-wider transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] flex items-center justify-center gap-2',
              glowFocus,
              couponLoading || !couponInput.trim()
                ? 'bg-black/[0.08] text-black/30 cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700 cursor-pointer',
            )}
          >
            {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
          </button>
        </div>
      )}
    </div>
  );

  // Loading inicial (plano público carregando)
  if (planQuery.isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-black animate-spin" />
      </div>
    );
  }

  // Plano inválido
  if (planQuery.isError || !plan) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
        <p className="text-black font-medium mb-2">Plano não encontrado</p>
        <p className="text-black/50 text-sm text-center max-w-[320px]">
          Esse plano não existe ou foi desativado. Entre em contato com o suporte.
        </p>
      </div>
    );
  }

  const pixDisplayAmount = paymentData?.amount ?? planPrice;
  const successRedirectTarget = resolveSuccessTarget(homeUrl, redirectUrl, saasOrigin);

  return (
    <div
      // [color-scheme:light]: o app é dark-first (globals.css põe color-scheme:dark
      // no html), o que faz o browser pintar controles nativos e o autofill com a
      // paleta escura — ilegível nas superfícies claras do checkout. Aqui a página
      // é sempre clara, independente do tema do app.
      className="min-h-screen bg-white lg:bg-[#0F172A] flex flex-col antialiased [color-scheme:light]"
      style={{ fontFamily: "'Open Sans', system-ui, sans-serif" }}
    >
      {/* Header Mobile */}
      {step !== 'success' && (
        <header className="h-14 flex items-center justify-between px-4 border-b border-black/[0.08] bg-white lg:hidden">
          <button
            onClick={() => {
              if (step === 'method') navigate(-1);
              else if (step === 'payment') setStep('method');
              else if (step === 'pix') setStep('method');
              else if (step === 'review') setStep('payment');
              else setStep('method');
            }}
            aria-label={step === 'method' ? 'Cancelar' : 'Voltar'}
            className={cn(
              'text-[17px] font-normal text-[#007AFF] active:opacity-60 cursor-pointer transition-opacity duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px] px-2 py-1 -ml-2 active:scale-[0.97]',
              glowFocus,
            )}
          >
            {step === 'method' ? 'Cancelar' : 'Voltar'}
          </button>
          <h1 className="text-[17px] font-semibold text-black tracking-[-0.01em]">Checkout</h1>
          <div className="w-14" />
        </header>
      )}

      {/* Stepper - só mobile */}
      {step !== 'success' && (
        <div className="flex items-center justify-center py-4 px-4 lg:hidden">
          {/* Step 1 - Plano (completed) */}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#34C759] flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
            </div>
            <span className="text-[13px] text-black/40 font-normal">Plano</span>
          </div>

          <div className="w-6 h-px bg-black/[0.12] mx-3" />

          {/* Step 2 - Método/Pagamento */}
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center',
                step === 'method' || step === 'payment' || step === 'pix'
                  ? 'bg-black'
                  : 'bg-[#34C759]',
              )}
            >
              {step === 'method' || step === 'payment' || step === 'pix' ? (
                <span className="text-white text-[13px] font-semibold">2</span>
              ) : (
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              )}
            </div>
            <span
              className={cn(
                'text-[13px] font-normal',
                step === 'method' || step === 'payment' || step === 'pix'
                  ? 'font-semibold text-black'
                  : 'text-black/40',
              )}
            >
              Pagamento
            </span>
          </div>

          <div className="w-6 h-px bg-black/[0.12] mx-3" />

          {/* Step 3 - Revisão/Confirmação */}
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center',
                step === 'review' || step === 'processing' ? 'bg-black' : 'bg-black/[0.08]',
              )}
            >
              <span
                className={cn(
                  'text-[13px] font-semibold',
                  step === 'review' || step === 'processing' ? 'text-white' : 'text-black/40',
                )}
              >
                3
              </span>
            </div>
            <span
              className={cn(
                'text-[13px] font-normal',
                step === 'review' || step === 'processing'
                  ? 'font-semibold text-black'
                  : 'text-black/40',
              )}
            >
              Confirmar
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <AnimatePresence mode="popLayout">
        {/* STEP: Seleção de Método de Pagamento */}
        {step === 'method' && (
          <motion.div
            key="method"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 px-4 pt-8 pb-28 lg:pb-20 lg:pt-12 lg:px-8 lg:bg-[#F5F1EC] lg:min-h-screen"
          >
            {/* Desktop Layout — Linoge style v2 */}
            <div className="hidden lg:block max-w-[1280px] mx-auto px-8">
              {/* Top bar com logo + voltar */}
              <div className="flex items-center justify-between mb-10">
                <button
                  onClick={() => navigate(-1)}
                  aria-label="Voltar"
                  className={cn(
                    'text-[13px] uppercase tracking-[0.15em] text-black/50 hover:text-black flex items-center gap-2 transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-medium cursor-pointer rounded-[6px] px-1 py-0.5 active:scale-[0.97]',
                    glowFocus,
                  )}
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                  Voltar
                </button>
                {homeUrl ? (
                  <a
                    href={homeUrl}
                    aria-label="Ir para a página inicial"
                    className={cn(
                      'flex items-center gap-2 hover:opacity-70 transition-opacity duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px]',
                      glowFocus,
                    )}
                  >
                    <img src="/logo-opus-ico.png" alt="" className="h-8 w-8 rounded-full" />
                    <span className="text-[15px] font-bold text-black tracking-tight">
                      {saasName ?? 'Opus Owl'}
                    </span>
                  </a>
                ) : (
                  <div className="flex items-center gap-2">
                    <img src="/logo-opus-ico.png" alt="" className="h-8 w-8 rounded-full" />
                    <span className="text-[15px] font-bold text-black tracking-tight">
                      {saasName ?? 'Opus Owl'}
                    </span>
                  </div>
                )}
                <div className="text-[13px] uppercase tracking-[0.15em] text-black/40 font-medium flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                  Pagamento seguro
                </div>
              </div>

              {/* Title editorial com brushstroke background — DRAW EFFECT */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EXPO_OUT }}
                className="relative mb-16 h-[200px] flex items-center"
              >
                {/* Brushstroke background — clip-path draw (3s ease-out) */}
                <motion.img
                  initial={{ clipPath: 'inset(0 100% 0 0)', opacity: 0 }}
                  animate={{ clipPath: 'inset(0 0% 0 0)', opacity: 0.75 }}
                  transition={{
                    clipPath: { duration: 2.8, ease: EXPO_OUT, delay: 0.3 },
                    opacity: { duration: 0.6, ease: EXPO_OUT, delay: 0.3 },
                  }}
                  src="/checkout-bg.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none mix-blend-multiply"
                  style={{
                    maskImage:
                      'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                  }}
                />
                {/* Cream halo atrás do texto pra dar contraste sutil */}
                <div
                  aria-hidden="true"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-[55%] h-[110%] pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse at left center, rgba(245,241,236,0.85) 0%, rgba(245,241,236,0.6) 40%, rgba(245,241,236,0) 75%)',
                  }}
                />
                <motion.h1
                  initial={{ opacity: 0, letterSpacing: '0em' }}
                  animate={{ opacity: 1, letterSpacing: '-0.04em' }}
                  transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                  className="relative z-10 text-[100px] font-black text-black leading-[0.85] mix-blend-multiply"
                  style={{ fontStretch: 'condensed' }}
                >
                  CHECKOUT
                </motion.h1>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_440px] gap-20 items-start">
                {/* ============================================ */}
                {/* COLUNA ESQUERDA — Formulário (stagger reveal) */}
                {/* ============================================ */}
                <motion.div variants={containerVariants} initial="hidden" animate="show">
                  {/* SECTION: Informação do plano (read-only) */}
                  <motion.div variants={itemVariants} className="mb-12">
                    <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                      Plano selecionado
                    </h2>
                    <div className="h-px bg-black mb-6" />

                    <div className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-[14px] text-black/40 uppercase tracking-wider mb-1">
                          {cycleTitle}
                        </p>
                        <p className="text-[28px] font-black text-black tracking-tight leading-none">
                          {planName}
                        </p>
                      </div>
                      {hasAnnual && (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setCycleChoice('MONTHLY')}
                            aria-pressed={billingCycle === 'MONTHLY'}
                            className={cn(
                              'text-[12px] uppercase tracking-[0.15em] font-medium cursor-pointer transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px] px-1',
                              glowFocus,
                              billingCycle === 'MONTHLY'
                                ? 'text-black underline underline-offset-4'
                                : 'text-black/40 hover:text-black/70',
                            )}
                          >
                            Mensal
                          </button>
                          <span className="w-px h-3 bg-black/20" />
                          <button
                            onClick={() => setCycleChoice('YEARLY')}
                            aria-pressed={billingCycle === 'YEARLY'}
                            className={cn(
                              'text-[12px] uppercase tracking-[0.15em] font-medium cursor-pointer transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px] px-1',
                              glowFocus,
                              billingCycle === 'YEARLY'
                                ? 'text-black underline underline-offset-4'
                                : 'text-black/40 hover:text-black/70',
                            )}
                          >
                            Anual
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* SECTION: Seus dados (público — substitui o useAuth do original) */}
                  <motion.div variants={itemVariants} className="mb-12">
                    <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                      Seus dados
                    </h2>
                    <div className="h-px bg-black mb-6" />

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                          Nome completo
                        </label>
                        <input
                          type="text"
                          autoComplete="name"
                          value={buyerName}
                          onChange={(e) => setBuyerName(e.target.value)}
                          placeholder="Maria da Silva"
                          className="w-full h-[50px] px-4 bg-white border border-black/[0.15] rounded-[10px] text-[16px] text-black placeholder:text-black/30 shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]"
                        />
                      </div>
                      <div>
                        <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                          E-mail
                        </label>
                        <input
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          value={buyerEmail}
                          onChange={(e) => setBuyerEmail(e.target.value)}
                          placeholder="voce@empresa.com"
                          className="w-full h-[50px] px-4 bg-white border border-black/[0.15] rounded-[10px] text-[16px] text-black placeholder:text-black/30 shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]"
                        />
                      </div>
                    </div>
                  </motion.div>

                  {/* SECTION: Forma de pagamento */}
                  <motion.div variants={itemVariants}>
                    <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                      Forma de pagamento
                    </h2>
                    <div className="h-px bg-black mb-6" />

                    <div className="space-y-3">
                      {/* PIX */}
                      <motion.button
                        onClick={() => setSelectedMethod('PIX')}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.99 }}
                        transition={{ duration: 0.2, ease: EXPO_OUT }}
                        aria-label="Selecionar PIX como forma de pagamento"
                        aria-pressed={selectedMethod === 'PIX'}
                        className={cn(
                          'w-full px-6 py-5 border rounded-[16px] bg-white flex items-center gap-5 text-left group cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
                          glowFocus,
                          selectedMethod === 'PIX'
                            ? 'border-teal-600 border-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
                            : 'border-black/[0.15] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:border-teal-600/40 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
                        )}
                      >
                        {/* Radio com checkmark draw */}
                        <div
                          className={cn(
                            'w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ease-[cubic-bezier(0.19,1,0.22,1)]',
                            selectedMethod === 'PIX'
                              ? 'border-teal-600 bg-teal-600'
                              : 'border-black/30',
                          )}
                        >
                          {selectedMethod === 'PIX' && (
                            <motion.svg
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.3, ease: EXPO_OUT }}
                              viewBox="0 0 16 16"
                              className="w-3 h-3"
                            >
                              <motion.path
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.4, ease: EXPO_OUT, delay: 0.1 }}
                                d="M3 8 L7 12 L13 4"
                                stroke="white"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                            </motion.svg>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <p className="text-[14px] font-bold text-black uppercase tracking-wide">
                              PIX
                            </p>
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-bold uppercase tracking-wider rounded-[6px]">
                              Instantâneo
                            </span>
                          </div>
                          <p className="text-[12px] text-black/50 mt-1">
                            Aprovação imediata · Sem taxas adicionais
                          </p>
                        </div>

                        {/* Logo PIX — cor oficial BCB via branded-icon */}
                        <PixIcon size={40} aria-label="" className="flex-shrink-0" />
                      </motion.button>

                      {/* Cartão de Crédito */}
                      <motion.button
                        onClick={() => setSelectedMethod('CREDIT_CARD')}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.99 }}
                        transition={{ duration: 0.2, ease: EXPO_OUT }}
                        aria-label="Selecionar Cartão de Crédito como forma de pagamento"
                        aria-pressed={selectedMethod === 'CREDIT_CARD'}
                        className={cn(
                          'w-full px-6 py-5 border rounded-[16px] bg-white flex items-center gap-5 text-left group cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
                          glowFocus,
                          selectedMethod === 'CREDIT_CARD'
                            ? 'border-teal-600 border-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
                            : 'border-black/[0.15] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:border-teal-600/40 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
                        )}
                      >
                        {/* Radio com checkmark draw */}
                        <div
                          className={cn(
                            'w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ease-[cubic-bezier(0.19,1,0.22,1)]',
                            selectedMethod === 'CREDIT_CARD'
                              ? 'border-teal-600 bg-teal-600'
                              : 'border-black/30',
                          )}
                        >
                          {selectedMethod === 'CREDIT_CARD' && (
                            <motion.svg
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.3, ease: EXPO_OUT }}
                              viewBox="0 0 16 16"
                              className="w-3 h-3"
                            >
                              <motion.path
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.4, ease: EXPO_OUT, delay: 0.1 }}
                                d="M3 8 L7 12 L13 4"
                                stroke="white"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                            </motion.svg>
                          )}
                        </div>

                        <div className="flex-1">
                          <p className="text-[14px] font-bold text-black uppercase tracking-wide">
                            Cartão de Crédito
                          </p>
                          <p className="text-[12px] text-black/50 mt-1">
                            Visa, Mastercard, Elo · Cobrança recorrente automática
                          </p>
                        </div>

                        {/* Bandeiras */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <VisaIcon size={28} />
                          <MasterCardIcon size={28} />
                        </div>
                      </motion.button>
                    </div>
                  </motion.div>

                  {/* Big black CTA — Linoge style + magnetic */}
                  <motion.div variants={itemVariants}>
                    <motion.button
                      onClick={() => {
                        if (!selectedMethod) {
                          toast.error('Selecione uma forma de pagamento');
                          return;
                        }
                        handleSelectMethod(selectedMethod);
                      }}
                      disabled={!selectedMethod}
                      onMouseMove={selectedMethod ? handleCtaMouseMove : undefined}
                      onMouseLeave={handleCtaMouseLeave}
                      whileTap={selectedMethod ? { scale: 0.97 } : undefined}
                      style={selectedMethod ? { x: ctaSpringX, y: ctaSpringY } : undefined}
                      className={cn(
                        'w-full mt-10 h-14 rounded-[10px] flex items-center justify-center gap-3 font-bold text-[13px] uppercase tracking-[0.2em] relative overflow-hidden group transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
                        glowFocus,
                        selectedMethod
                          ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white hover:from-teal-700 hover:to-teal-600 shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] cursor-pointer'
                          : 'bg-black/[0.08] text-black/30 cursor-not-allowed',
                      )}
                    >
                      {selectedMethod && (
                        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)]" />
                      )}
                      <span className="relative z-10 flex items-center gap-3">
                        Continuar para pagamento
                        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                      </span>
                    </motion.button>
                  </motion.div>

                  {/* Trust signals */}
                  <motion.div
                    variants={itemVariants}
                    className="mt-6 flex items-center justify-center gap-2 text-[11px] text-black/40 uppercase tracking-wider"
                  >
                    <AsaasIcon size={12} aria-label="" />
                    Pagamento seguro via Asaas
                  </motion.div>
                </motion.div>

                {/* ============================================ */}
                {/* COLUNA DIREITA — Order Summary (sticky + slide-in) */}
                {/* ============================================ */}
                <motion.div
                  variants={sidebarVariants}
                  initial="hidden"
                  animate="show"
                  className="lg:sticky lg:top-12"
                >
                  <div className="bg-white border border-black/[0.08] rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] p-8">
                    <div className="flex items-center justify-between mb-1">
                      <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black">
                        Resumo
                      </h2>
                      <span className="text-[11px] text-black/40 uppercase tracking-wider">
                        1 item
                      </span>
                    </div>
                    <div className="h-px bg-black mb-6" />

                    {/* Item: o plano */}
                    <div className="flex items-start gap-4 pb-6 border-b border-black/[0.08]">
                      {/* Thumb editorial — monograma da inicial do plano + breathing */}
                      <motion.div
                        animate={{ scale: [1, 1.02, 1] }}
                        transition={{ duration: 4, ease: 'easeInOut', repeat: Infinity }}
                        className="w-20 h-20 bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC] border border-black/[0.12] rounded-[10px] flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                      >
                        {/* Decoração: linha diagonal sutil */}
                        <div
                          className="absolute inset-0 opacity-[0.04]"
                          style={{
                            backgroundImage:
                              'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                          }}
                        />
                        <span
                          className="relative z-10 text-[56px] font-black text-transparent bg-clip-text bg-gradient-to-br from-teal-700 to-teal-900 leading-none tracking-tighter"
                          style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontStyle: 'italic',
                          }}
                        >
                          {planName.charAt(0)}
                        </span>
                      </motion.div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-bold text-black leading-tight">
                          Plano {planName}
                        </p>
                        <div className="mt-2 space-y-1">
                          <p className="text-[11px] text-black/50 uppercase tracking-wider">
                            <span className="text-black/40">Ciclo:</span>{' '}
                            <span className="text-black/70 font-medium">
                              {cycleShort}
                            </span>
                          </p>
                          <p className="text-[11px] text-black/50 uppercase tracking-wider">
                            <span className="text-black/40">Renovação:</span>{' '}
                            <span className="text-black/70 font-medium">{isOneTime ? 'Não renova' : 'Automática'}</span>
                          </p>
                          <p className="text-[11px] text-black/50 uppercase tracking-wider">
                            <span className="text-black/40">Qtd:</span>{' '}
                            <span className="text-black/70 font-medium">1</span>
                          </p>
                        </div>
                      </div>
                      <p className="text-[14px] font-bold text-black tabular-nums whitespace-nowrap">
                        R$ {formatMoney(planPrice)}
                      </p>
                    </div>

                    {/* Breakdown */}
                    <div className="py-6 space-y-3 border-b border-black/[0.08]">
                      {billingCycle === 'YEARLY' && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] text-black/50 uppercase tracking-wider">
                              Subtotal (12 meses)
                            </span>
                            <span className="text-[13px] text-black/40 tabular-nums line-through">
                              R$ {formatMoney(monthlyPrice * 12)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] text-teal-700 font-bold uppercase tracking-wider">
                              Desconto anual
                            </span>
                            <span className="text-[13px] text-teal-700 font-bold tabular-nums">
                              −R$ {formatMoney(Math.max(0, monthlyPrice * 12 - planPrice))}
                            </span>
                          </div>
                        </>
                      )}
                      {billingCycle === 'MONTHLY' && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] text-black/50 uppercase tracking-wider">
                              Subtotal
                            </span>
                            <span className="text-[13px] text-black/70 tabular-nums">
                              R$ {formatMoney(planPrice)}
                            </span>
                          </div>
                          {!couponApplied && (
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] text-black/50 uppercase tracking-wider">
                                Desconto
                              </span>
                              <span className="text-[13px] text-black/70 tabular-nums">
                                R$ 0,00
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      {couponApplied && (
                        <div className="flex items-center justify-between">
                          <span className="text-[12px] text-teal-700 font-bold uppercase tracking-wider">
                            Cupom {couponApplied.code}
                          </span>
                          <span className="text-[13px] text-teal-700 font-bold tabular-nums">
                            −R$ {formatMoney(couponDiscount)}
                          </span>
                        </div>
                      )}
                    </div>

                    {renderCouponField()}

                    {/* Total — counter animado */}
                    <div className="py-6 flex items-center justify-between">
                      <span className="text-[18px] font-black text-black uppercase tracking-tight">
                        Total
                      </span>
                      <div className="text-right">
                        <p className="text-[28px] font-black text-black tabular-nums leading-none tracking-tight">
                          R$ {formatMoney(animatedTotal)}
                        </p>
                        {billingCycle === 'YEARLY' && (
                          <p className="text-[10px] text-black/40 mt-1.5 uppercase tracking-wider">
                            ≈ R$ {Math.round(planPrice / 12).toLocaleString('pt-BR')} / mês
                          </p>
                        )}
                        {plan.trial_days > 0 && (
                          <p className="text-[10px] text-teal-700 font-bold mt-1.5 uppercase tracking-wider">
                            {plan.trial_days} dias grátis — cobrança só depois
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Trust list */}
                    <div className="space-y-2.5 border-t border-black/[0.08] mt-2 pt-6">
                      {[
                        isOneTime ? 'Cobrança única, sem renovação' : 'Cancele a qualquer momento',
                        'Suporte incluso no plano',
                        'Ativação imediata após pagamento',
                      ].map((item) => (
                        <div key={item} className="flex items-center gap-2.5">
                          <div className="w-4 h-4 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                            <Check className="w-2.5 h-2.5 text-teal-600" strokeWidth={3.5} />
                          </div>
                          <span className="text-[11px] text-black/60 uppercase tracking-wider">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Mobile Layout — Linoge style */}
            <div className="lg:hidden bg-[#F5F1EC] -mx-4 -mt-8 px-5 pt-8 pb-32 min-h-screen">
              {/* Top: voltar + logo */}
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={() => navigate(-1)}
                  aria-label="Voltar"
                  className={cn(
                    'text-[11px] uppercase tracking-[0.15em] text-black/50 active:text-black flex items-center gap-1.5 font-medium cursor-pointer transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] active:scale-[0.97] rounded-[6px] px-1 py-0.5',
                    glowFocus,
                  )}
                >
                  <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
                  Voltar
                </button>
                {homeUrl ? (
                  <a
                    href={homeUrl}
                    aria-label="Ir para a página inicial"
                    className={cn(
                      'flex items-center gap-1.5 active:opacity-70 transition-opacity duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px]',
                      glowFocus,
                    )}
                  >
                    <img src="/logo-opus-ico.png" alt="" className="h-6 w-6 rounded-full" />
                    <span className="text-[13px] font-bold text-black tracking-tight">
                      {saasName ?? 'Opus Owl'}
                    </span>
                  </a>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <img src="/logo-opus-ico.png" alt="" className="h-6 w-6 rounded-full" />
                    <span className="text-[13px] font-bold text-black tracking-tight">
                      {saasName ?? 'Opus Owl'}
                    </span>
                  </div>
                )}
                <div className="w-12" />
              </div>

              {/* Title editorial mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="relative h-[120px] flex items-center mb-8"
              >
                <motion.img
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 0.7, x: 0 }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                  src="/checkout-bg.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none mix-blend-multiply"
                  style={{
                    maskImage:
                      'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
                    WebkitMaskImage:
                      'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
                  }}
                />
                <div
                  aria-hidden="true"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-[60%] h-[110%] pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse at left center, rgba(245,241,236,0.85) 0%, rgba(245,241,236,0.5) 40%, rgba(245,241,236,0) 75%)',
                  }}
                />
                <h1
                  className="relative z-10 text-[52px] font-black text-black tracking-[-0.04em] leading-[0.85] mix-blend-multiply"
                  style={{ fontStretch: 'condensed' }}
                >
                  CHECKOUT
                </h1>
              </motion.div>

              {/* SECTION: Plano selecionado */}
              <div className="mb-8">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                  Plano selecionado
                </h2>
                <div className="h-px bg-black mb-4" />

                <div className="flex items-center justify-between py-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-black/40 uppercase tracking-wider mb-0.5">
                      {cycleTitle}
                    </p>
                    <p className="text-[22px] font-black text-black tracking-tight leading-none">
                      {planName}
                    </p>
                  </div>
                  {hasAnnual && (
                    <div className="flex items-center gap-2.5">
                      <button
                        onClick={() => setCycleChoice('MONTHLY')}
                        aria-pressed={billingCycle === 'MONTHLY'}
                        className={cn(
                          'text-[11px] uppercase tracking-[0.15em] font-medium cursor-pointer transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px] px-1 active:scale-[0.97]',
                          glowFocus,
                          billingCycle === 'MONTHLY'
                            ? 'text-black underline underline-offset-4'
                            : 'text-black/40',
                        )}
                      >
                        Mensal
                      </button>
                      <span className="w-px h-3 bg-black/20" />
                      <button
                        onClick={() => setCycleChoice('YEARLY')}
                        aria-pressed={billingCycle === 'YEARLY'}
                        className={cn(
                          'text-[11px] uppercase tracking-[0.15em] font-medium cursor-pointer transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px] px-1 active:scale-[0.97]',
                          glowFocus,
                          billingCycle === 'YEARLY'
                            ? 'text-black underline underline-offset-4'
                            : 'text-black/40',
                        )}
                      >
                        Anual
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION: Seus dados */}
              <div className="mb-8">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                  Seus dados
                </h2>
                <div className="h-px bg-black mb-4" />

                <div className="space-y-4">
                  <div>
                    <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                      Nome completo
                    </label>
                    <input
                      type="text"
                      autoComplete="name"
                      value={buyerName}
                      onChange={(e) => setBuyerName(e.target.value)}
                      placeholder="Maria da Silva"
                      className="w-full h-[50px] px-4 bg-white border border-black/[0.15] rounded-[10px] text-[17px] text-black placeholder:text-black/30 shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]"
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                      E-mail
                    </label>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      placeholder="voce@empresa.com"
                      className="w-full h-[50px] px-4 bg-white border border-black/[0.15] rounded-[10px] text-[17px] text-black placeholder:text-black/30 shadow-[0_1px_2px_rgba(0,0,0,0.05)] focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION: Forma de pagamento */}
              <div className="mb-8">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                  Forma de pagamento
                </h2>
                <div className="h-px bg-black mb-4" />

                <div className="space-y-3">
                  {/* PIX */}
                  <button
                    onClick={() => setSelectedMethod('PIX')}
                    aria-label="Selecionar PIX como forma de pagamento"
                    aria-pressed={selectedMethod === 'PIX'}
                    className={cn(
                      'w-full px-5 py-4 border rounded-[16px] bg-white transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] flex items-center gap-4 text-left cursor-pointer active:scale-[0.97]',
                      glowFocus,
                      selectedMethod === 'PIX'
                        ? 'border-teal-600 border-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
                        : 'border-black/[0.15] shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:border-teal-600/40',
                    )}
                  >
                    <div
                      className={cn(
                        'w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
                        selectedMethod === 'PIX' ? 'border-teal-600' : 'border-black/30',
                      )}
                    >
                      {selectedMethod === 'PIX' && (
                        <div className="w-2 h-2 rounded-full bg-teal-600" />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-bold text-black uppercase tracking-wide">
                          PIX
                        </p>
                        <span className="px-1.5 py-0.5 bg-teal-50 text-teal-700 text-[9px] font-bold uppercase tracking-wider rounded-[6px]">
                          Instantâneo
                        </span>
                      </div>
                      <p className="text-[11px] text-black/50 mt-0.5">
                        Aprovação imediata · Sem taxas
                      </p>
                    </div>

                    <PixIcon size={36} aria-label="" className="flex-shrink-0" />
                  </button>

                  {/* Cartão */}
                  <button
                    onClick={() => setSelectedMethod('CREDIT_CARD')}
                    aria-label="Selecionar Cartão de Crédito como forma de pagamento"
                    aria-pressed={selectedMethod === 'CREDIT_CARD'}
                    className={cn(
                      'w-full px-5 py-4 border rounded-[16px] bg-white transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] flex items-center gap-4 text-left cursor-pointer active:scale-[0.97]',
                      glowFocus,
                      selectedMethod === 'CREDIT_CARD'
                        ? 'border-teal-600 border-2 shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
                        : 'border-black/[0.15] shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:border-teal-600/40',
                    )}
                  >
                    <div
                      className={cn(
                        'w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
                        selectedMethod === 'CREDIT_CARD' ? 'border-teal-600' : 'border-black/30',
                      )}
                    >
                      {selectedMethod === 'CREDIT_CARD' && (
                        <div className="w-2 h-2 rounded-full bg-teal-600" />
                      )}
                    </div>

                    <div className="flex-1">
                      <p className="text-[13px] font-bold text-black uppercase tracking-wide">
                        Cartão de Crédito
                      </p>
                      <p className="text-[11px] text-black/50 mt-0.5">
                        Visa, Master, Elo · Recorrente
                      </p>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <VisaIcon size={24} />
                      <MasterCardIcon size={24} />
                    </div>
                  </button>
                </div>
              </div>

              {/* SECTION: Resumo (mobile fica embaixo) */}
              <div className="mb-8">
                <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                  Resumo
                </h2>
                <div className="h-px bg-black mb-4" />

                <div className="bg-white border border-black/[0.08] rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-5">
                  {/* Item */}
                  <div className="flex items-start gap-3 pb-4 border-b border-black/[0.08]">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC] border border-black/[0.12] rounded-[10px] flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                      <div
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                        }}
                      />
                      <span
                        className="relative z-10 text-[40px] font-black text-transparent bg-clip-text bg-gradient-to-br from-teal-700 to-teal-900 leading-none tracking-tighter"
                        style={{
                          fontFamily: "'Playfair Display', Georgia, serif",
                          fontStyle: 'italic',
                        }}
                      >
                        {planName.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-bold text-black leading-tight">
                        Plano {planName}
                      </p>
                      <p className="text-[10px] text-black/50 uppercase tracking-wider mt-1">
                        {cycleShort} · {isOneTime ? 'Sem renovação' : 'Renovação automática'}
                      </p>
                    </div>
                    <p className="text-[13px] font-bold text-black tabular-nums whitespace-nowrap">
                      R$ {formatMoney(planPrice)}
                    </p>
                  </div>

                  {/* Breakdown */}
                  <div className="py-4 space-y-2 border-b border-black/[0.08]">
                    {billingCycle === 'YEARLY' && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-black/50 uppercase tracking-wider">
                            Subtotal (12m)
                          </span>
                          <span className="text-[11px] text-black/40 tabular-nums line-through">
                            R$ {formatMoney(monthlyPrice * 12)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-teal-700 font-bold uppercase tracking-wider">
                            Desconto
                          </span>
                          <span className="text-[11px] text-teal-700 font-bold tabular-nums">
                            −R$ {formatMoney(Math.max(0, monthlyPrice * 12 - planPrice))}
                          </span>
                        </div>
                      </>
                    )}
                    {billingCycle === 'MONTHLY' && (
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-black/50 uppercase tracking-wider">
                          Subtotal
                        </span>
                        <span className="text-[11px] text-black/70 tabular-nums">
                          R$ {formatMoney(planPrice)}
                        </span>
                      </div>
                    )}
                    {couponApplied && (
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-teal-700 font-bold uppercase tracking-wider">
                          Cupom {couponApplied.code}
                        </span>
                        <span className="text-[11px] text-teal-700 font-bold tabular-nums">
                          −R$ {formatMoney(couponDiscount)}
                        </span>
                      </div>
                    )}
                  </div>

                  {renderCouponField()}

                  {/* Total */}
                  <div className="py-4 flex items-center justify-between">
                    <span className="text-[15px] font-black text-black uppercase tracking-tight">
                      Total
                    </span>
                    <div className="text-right">
                      <p className="text-[22px] font-black text-black tabular-nums leading-none">
                        R$ {formatMoney(effectivePrice)}
                      </p>
                      {billingCycle === 'YEARLY' && (
                        <p className="text-[9px] text-black/40 mt-1 uppercase tracking-wider">
                          ≈ R$ {Math.round(planPrice / 12).toLocaleString('pt-BR')}/mês
                        </p>
                      )}
                      {plan.trial_days > 0 && (
                        <p className="text-[9px] text-teal-700 font-bold mt-1 uppercase tracking-wider">
                          {plan.trial_days} dias grátis — cobrança só depois
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Trust mini */}
                  <div className="space-y-2 pt-3 border-t border-black/[0.08]">
                    {[
                      isOneTime ? 'Cobrança única, sem renovação' : 'Cancele a qualquer momento',
                      'Suporte incluso no plano',
                      'Ativação imediata',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                          <Check className="w-2 h-2 text-teal-600" strokeWidth={3.5} />
                        </div>
                        <span className="text-[10px] text-black/60 uppercase tracking-wider">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA fixo no bottom mobile */}
              <div className="fixed bottom-0 left-0 right-0 bg-[#F5F1EC] border-t border-black/[0.08] p-4 z-50 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <button
                  onClick={() => {
                    if (!selectedMethod) {
                      toast.error('Selecione uma forma de pagamento');
                      return;
                    }
                    handleSelectMethod(selectedMethod);
                  }}
                  disabled={!selectedMethod}
                  aria-label="Continuar para pagamento"
                  className={cn(
                    'w-full py-4 rounded-[10px] flex items-center justify-center gap-3 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-bold text-[12px] uppercase tracking-[0.2em] relative overflow-hidden',
                    glowFocus,
                    selectedMethod
                      ? 'bg-gradient-to-r from-teal-600 to-teal-500 text-white shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97] cursor-pointer'
                      : 'bg-black/[0.08] text-black/30 cursor-not-allowed',
                  )}
                >
                  <span className="flex items-center gap-2">
                    Continuar para pagamento
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>
                </button>
                <p className="flex items-center justify-center gap-1.5 mt-2 text-[9px] text-black/40 uppercase tracking-wider">
                  <AsaasIcon size={10} aria-label="" />
                  Pagamento seguro via Asaas
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP: PIX - QR Code e Código */}
        {step === 'pix' && (
          <motion.div
            key="pix"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 px-4 pt-8 pb-28 lg:pb-12 lg:pt-8 lg:px-8 lg:bg-slate-50 lg:min-h-screen"
          >
            {/* Desktop Layout */}
            <div className="hidden lg:block max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={() => setStep('method')}
                  aria-label="Voltar para seleção de método"
                  className={cn(
                    'text-[15px] text-slate-500 hover:text-slate-700 flex items-center gap-2 transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer rounded-[6px] px-1 py-0.5 active:scale-[0.97]',
                    glowFocus,
                  )}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </button>
              </div>

              <div className="bg-white rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] border border-black/[0.06] overflow-hidden">
                {/* Content */}
                <div className={pixExpired ? 'p-0' : 'p-10'}>
                  {pixLoading || !paymentData ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Loader2 className="w-10 h-10 text-slate-300 animate-spin mb-4" />
                      <p className="text-sm text-slate-400">Gerando QR Code PIX...</p>
                    </div>
                  ) : pixExpired ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      className="relative px-8 pt-12 pb-14 overflow-hidden"
                    >
                      {/* Brushstroke decorativo no canto superior */}
                      <motion.img
                        initial={{ clipPath: 'inset(0 100% 0 0)', opacity: 0 }}
                        animate={{ clipPath: 'inset(0 0% 0 0)', opacity: 0.5 }}
                        transition={{
                          clipPath: { duration: 1.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 },
                          opacity: { duration: 0.6, delay: 0.2 },
                        }}
                        src="/checkout-bg.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute -top-4 left-0 right-0 w-full h-32 object-cover object-center pointer-events-none select-none mix-blend-multiply"
                        style={{
                          maskImage:
                            'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
                          WebkitMaskImage:
                            'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
                        }}
                      />

                      {/* Top label */}
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="relative flex items-center gap-2 mb-12"
                      >
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50">
                          Tempo esgotado
                        </span>
                      </motion.div>

                      {/* Timer 00:00 GIGANTE — peça central */}
                      <div className="relative flex items-baseline justify-center mb-10">
                        <motion.div
                          initial={{ opacity: 0, y: 30, letterSpacing: '0em' }}
                          animate={{ opacity: 1, y: 0, letterSpacing: '-0.06em' }}
                          transition={{
                            duration: 1,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.4,
                          }}
                          className="relative inline-block"
                        >
                          {/* "00:00" gigante com strikethrough */}
                          <span
                            className="text-[140px] font-black text-black leading-[0.8] tabular-nums tracking-tighter block"
                            style={{ fontStretch: 'condensed', fontFeatureSettings: '"tnum"' }}
                          >
                            00:00
                          </span>
                          {/* Linha riscada animada */}
                          <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{
                              duration: 0.8,
                              ease: [0.16, 1, 0.3, 1],
                              delay: 1.1,
                            }}
                            className="absolute top-1/2 left-0 right-0 h-[3px] bg-red-500 origin-left"
                            style={{ transform: 'translateY(-50%) rotate(-2deg)' }}
                          />
                        </motion.div>
                      </div>

                      {/* Block info: 2 colunas asimétricas */}
                      <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
                        className="relative grid grid-cols-[minmax(0,1fr)_auto] gap-6 items-end mb-10"
                      >
                        {/* Esquerda: descrição */}
                        <div>
                          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-2">
                            O que aconteceu
                          </h3>
                          <div className="h-px bg-black mb-3 max-w-[60px]" />
                          <p className="text-[13px] text-black/60 leading-relaxed max-w-[300px]">
                            O código PIX expira por segurança. Você pode gerar um novo agora —{' '}
                            <span className="text-black/40">o anterior não vale mais</span>.
                          </p>
                        </div>
                      </motion.div>

                      {/* CTA full-width estilo Linoge */}
                      <motion.button
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
                        onClick={handleRegeneratePix}
                        whileTap={{ scale: 0.99 }}
                        aria-label="Gerar novo código PIX"
                        className={cn(
                          'relative overflow-hidden w-full h-14 bg-black text-white font-bold text-[12px] uppercase tracking-[0.25em] flex items-center justify-center gap-3 group hover:bg-black/85 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer rounded-[10px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
                          glowFocus,
                        )}
                      >
                        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[1200ms] ease-[cubic-bezier(0.19,1,0.22,1)]" />
                        <RefreshCw className="w-4 h-4 relative z-10" strokeWidth={2.5} />
                        <span className="relative z-10">Gerar novo código</span>
                      </motion.button>
                    </motion.div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      className="flex flex-col items-center max-w-[480px] mx-auto"
                    >
                      {/* Top: status + timer */}
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="w-full flex items-center justify-between mb-6"
                      >
                        <div className="flex items-center gap-2">
                          <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                          </span>
                          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50">
                            Aguardando pagamento
                          </span>
                        </div>
                        {pixSecondsLeft > 0 && (
                          <motion.div
                            key={pixSecondsLeft}
                            initial={{ scale: 1 }}
                            animate={
                              pixSecondsLeft <= 10
                                ? { scale: [1, 1.05, 1], x: [0, -2, 2, -2, 2, 0] }
                                : { scale: [1, 1.05, 1] }
                            }
                            transition={{ duration: 0.3, ease: EXPO_OUT }}
                            className={cn(
                              'font-mono text-[13px] font-bold tabular-nums transition-colors duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]',
                              pixSecondsLeft <= 60 ? 'text-red-600' : 'text-black/60',
                            )}
                          >
                            {Math.floor(pixSecondsLeft / 60)
                              .toString()
                              .padStart(2, '0')}
                            :{(pixSecondsLeft % 60).toString().padStart(2, '0')}
                          </motion.div>
                        )}
                      </motion.div>

                      {/* Title editorial centralizado */}
                      <motion.h2
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                        className="text-[42px] font-black text-black leading-[0.9] mb-2 text-center"
                        style={{ fontStretch: 'condensed', letterSpacing: '-0.04em' }}
                      >
                        PAGUE COM PIX
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="text-[13px] text-black/50 mb-8 text-center"
                      >
                        Escaneie o QR Code ou cole o código no app do seu banco.
                      </motion.p>

                      {/* QR Code centralizado */}
                      {paymentData.pixQrCodeUrl && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
                          className="mb-8"
                        >
                          <div className="relative bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC] border border-black/[0.12] rounded-[16px] p-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                            <div
                              className="absolute inset-0 opacity-[0.03] rounded-[16px] pointer-events-none"
                              style={{
                                backgroundImage:
                                  'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                              }}
                            />
                            <img
                              src={paymentData.pixQrCodeUrl}
                              alt="QR Code PIX"
                              width={224}
                              height={224}
                              className="relative w-56 h-56 block"
                            />
                          </div>
                        </motion.div>
                      )}

                      {/* Valor centralizado */}
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="mb-8 text-center"
                      >
                        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mb-1">
                          Valor a pagar
                        </h3>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-[14px] font-bold text-black/40">R$</span>
                          <span
                            className="text-[44px] font-black text-black leading-none tabular-nums"
                            style={{ letterSpacing: '-0.03em' }}
                          >
                            {formatMoney(pixDisplayAmount)}
                          </span>
                        </div>
                      </motion.div>

                      {/* Código PIX */}
                      {paymentData.pixCopiaECola && (
                        <motion.div
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: 0.6 }}
                          className="w-full mb-5"
                        >
                          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                            Código PIX copia e cola
                          </h3>
                          <div className="h-px bg-black mb-3" />

                          <div className="bg-[#F5F1EC] border border-black/[0.08] rounded-[10px] p-3 mb-3 max-h-[80px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-black/20">
                            <p className="text-[10px] text-black/60 font-mono break-all leading-relaxed">
                              {paymentData.pixCopiaECola}
                            </p>
                          </div>

                          <motion.button
                            onClick={() => void handleCopyPix()}
                            whileTap={{ scale: 0.99 }}
                            aria-label={pixCopied ? 'Código PIX copiado' : 'Copiar código PIX'}
                            className={cn(
                              'relative overflow-hidden w-full h-12 font-bold text-[12px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] group cursor-pointer rounded-[10px]',
                              glowFocus,
                              pixCopied
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                                : 'bg-black text-white hover:bg-black/85 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]',
                            )}
                          >
                            {!pixCopied && (
                              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[1200ms] ease-[cubic-bezier(0.19,1,0.22,1)]" />
                            )}
                            {pixCopied ? (
                              <>
                                <Check className="w-4 h-4 relative z-10" strokeWidth={2.5} />
                                <span className="relative z-10">Copiado com sucesso</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 relative z-10" strokeWidth={2.5} />
                                <span className="relative z-10">Copiar código PIX</span>
                              </>
                            )}
                          </motion.button>
                        </motion.div>
                      )}

                      {/* Instruções */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.7 }}
                        className="w-full mt-2 pt-4 border-t border-black/[0.06]"
                      >
                        <p className="text-[11px] text-black/40 leading-relaxed text-center">
                          Abra o app do seu banco, escolha PIX, escaneie o QR Code (ou cole o
                          código) e confirme. A ativação é{' '}
                          <strong className="text-black/60">automática</strong>.
                        </p>
                      </motion.div>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="lg:hidden">
              {/* Header — esconder quando expirado */}
              {!pixExpired && (
                <div className="text-center mb-6">
                  <h2 className="text-[22px] font-bold text-slate-900">Pague com PIX</h2>
                  <p className="text-[14px] text-slate-400 mt-1">
                    Escaneie o QR Code ou copie o código
                  </p>
                </div>
              )}

              {pixLoading || !paymentData ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-10 h-10 text-slate-300 animate-spin mb-4" />
                  <p className="text-[14px] text-slate-400">Gerando QR Code PIX...</p>
                </div>
              ) : pixExpired ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="relative flex flex-col items-center justify-center min-h-[70vh] py-8 overflow-hidden"
                >
                  {/* Brushstroke decorativo no topo (mesmo do desktop) */}
                  <motion.img
                    initial={{ clipPath: 'inset(0 100% 0 0)', opacity: 0 }}
                    animate={{ clipPath: 'inset(0 0% 0 0)', opacity: 0.5 }}
                    transition={{
                      clipPath: { duration: 1.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 },
                      opacity: { duration: 0.6, delay: 0.2 },
                    }}
                    src="/checkout-bg.png"
                    alt=""
                    aria-hidden="true"
                    className="absolute -top-4 left-0 right-0 w-full h-24 object-cover object-center pointer-events-none select-none mix-blend-multiply"
                    style={{
                      maskImage:
                        'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                      WebkitMaskImage:
                        'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
                    }}
                  />

                  {/* Top label */}
                  <motion.div
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="relative flex items-center gap-2 mb-6 self-center mt-8"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-black/50">
                      Tempo esgotado
                    </span>
                  </motion.div>

                  {/* Timer 00:00 GIGANTE — centralizado */}
                  <div className="relative flex justify-center mb-8">
                    <motion.div
                      initial={{ opacity: 0, y: 20, letterSpacing: '0em' }}
                      animate={{ opacity: 1, y: 0, letterSpacing: '-0.06em' }}
                      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
                      className="relative inline-block"
                    >
                      <span
                        className="text-[88px] font-black text-black leading-[0.8] tabular-nums tracking-tighter block"
                        style={{ fontStretch: 'condensed', fontFeatureSettings: '"tnum"' }}
                      >
                        00:00
                      </span>
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 1.1 }}
                        className="absolute top-1/2 left-0 right-0 h-[2.5px] bg-red-500 origin-left"
                        style={{ transform: 'translateY(-50%) rotate(-2deg)' }}
                      />
                    </motion.div>
                  </div>

                  {/* Block info */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
                    className="mb-7 w-full max-w-[300px]"
                  >
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black mb-2 text-center">
                      O que aconteceu
                    </h3>
                    <div className="h-px bg-black mb-3 mx-auto w-[50px]" />
                    <p className="text-[12px] text-black/60 leading-relaxed text-center">
                      O código PIX expira por segurança. Gere um novo —{' '}
                      <span className="text-black/40">o anterior não vale mais</span>.
                    </p>
                  </motion.div>

                  {/* CTA full-width preto */}
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
                    onClick={handleRegeneratePix}
                    whileTap={{ scale: 0.99 }}
                    aria-label="Gerar novo código PIX"
                    className={cn(
                      'relative overflow-hidden w-full max-w-[300px] py-4 bg-black text-white font-bold text-[11px] uppercase tracking-[0.25em] flex items-center justify-center gap-3 active:bg-black/85 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer rounded-[10px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.97]',
                      glowFocus,
                    )}
                  >
                    <RefreshCw className="w-4 h-4 relative z-10" strokeWidth={2.5} />
                    <span className="relative z-10">Gerar novo código</span>
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center"
                >
                  {/* Status + timer no topo */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="w-full flex items-center justify-between mb-5"
                  >
                    <div className="flex items-center gap-2">
                      <span className="flex h-2 w-2 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                      </span>
                      <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-black/50">
                        Aguardando
                      </span>
                    </div>
                    {pixSecondsLeft > 0 && (
                      <motion.div
                        key={pixSecondsLeft}
                        initial={{ scale: 1 }}
                        animate={
                          pixSecondsLeft <= 10
                            ? { scale: [1, 1.05, 1], x: [0, -2, 2, -2, 2, 0] }
                            : { scale: [1, 1.05, 1] }
                        }
                        transition={{ duration: 0.3, ease: EXPO_OUT }}
                        className={cn(
                          'font-mono text-[12px] font-bold tabular-nums transition-colors duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]',
                          pixSecondsLeft <= 60 ? 'text-red-600' : 'text-black/60',
                        )}
                      >
                        {Math.floor(pixSecondsLeft / 60)
                          .toString()
                          .padStart(2, '0')}
                        :{(pixSecondsLeft % 60).toString().padStart(2, '0')}
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Title editorial */}
                  <motion.h2
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
                    className="text-[32px] font-black text-black leading-[0.9] mb-1.5 text-center"
                    style={{ fontStretch: 'condensed', letterSpacing: '-0.04em' }}
                  >
                    PAGUE COM PIX
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-[12px] text-black/50 mb-6 text-center"
                  >
                    Escaneie o QR Code ou cole o código no app do banco.
                  </motion.p>

                  {/* QR Code centralizado */}
                  {paymentData.pixQrCodeUrl && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
                      className="mb-6"
                    >
                      <div className="relative bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC] border border-black/[0.12] rounded-[16px] p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div
                          className="absolute inset-0 opacity-[0.03] rounded-[16px] pointer-events-none"
                          style={{
                            backgroundImage:
                              'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                          }}
                        />
                        <img
                          src={paymentData.pixQrCodeUrl}
                          alt="QR Code PIX"
                          width={220}
                          height={220}
                          className="relative w-[220px] h-[220px] block"
                        />
                      </div>
                    </motion.div>
                  )}

                  {/* Valor centralizado */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="mb-7 text-center"
                  >
                    <h3 className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 mb-0.5">
                      Valor a pagar
                    </h3>
                    <div className="flex items-baseline justify-center gap-1.5">
                      <span className="text-[12px] font-bold text-black/40">R$</span>
                      <span
                        className="text-[34px] font-black text-black leading-none tabular-nums"
                        style={{ letterSpacing: '-0.03em' }}
                      >
                        {formatMoney(pixDisplayAmount)}
                      </span>
                    </div>
                  </motion.div>

                  {/* Código PIX */}
                  {paymentData.pixCopiaECola && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                      className="w-full mb-5"
                    >
                      <h3 className="text-[9px] font-bold uppercase tracking-[0.2em] text-black mb-1">
                        Código copia e cola
                      </h3>
                      <div className="h-px bg-black mb-2.5" />

                      <div className="bg-[#F5F1EC] border border-black/[0.08] rounded-[10px] p-2.5 mb-3 max-h-[70px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-black/20">
                        <p className="text-[10px] text-black/60 font-mono break-all leading-relaxed">
                          {paymentData.pixCopiaECola}
                        </p>
                      </div>

                      <motion.button
                        onClick={() => void handleCopyPix()}
                        whileTap={{ scale: 0.99 }}
                        aria-label={pixCopied ? 'Código PIX copiado' : 'Copiar código PIX'}
                        className={cn(
                          'w-full h-12 font-bold text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer rounded-[10px] active:scale-[0.97]',
                          glowFocus,
                          pixCopied
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-black text-white active:bg-black/85 shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
                        )}
                      >
                        {pixCopied ? (
                          <>
                            <Check className="w-4 h-4" strokeWidth={2.5} />
                            <span>Copiado</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" strokeWidth={2.5} />
                            <span>Copiar código PIX</span>
                          </>
                        )}
                      </motion.button>
                    </motion.div>
                  )}

                  {/* Instruções */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                    className="w-full mt-2 pt-4 border-t border-black/[0.06]"
                  >
                    <p className="text-[10px] text-black/40 leading-relaxed text-center">
                      Abra o app do banco, escolha PIX, escaneie o QR (ou cole o código) e confirme.
                      Ativação <strong className="text-black/60">automática</strong>.
                    </p>
                  </motion.div>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}

        {/* STEP: Seleção de Pagamento (form do cartão) */}
        {step === 'payment' && (
          <motion.div
            key="payment"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 px-4 pt-8 pb-28 lg:pb-12 lg:pt-8 lg:px-8 lg:bg-slate-50 lg:min-h-screen"
          >
            {/* ========== DESKTOP LAYOUT ========== */}
            <div className="hidden lg:block max-w-5xl mx-auto">
              {/* Header Desktop */}
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={() => setStep('method')}
                  aria-label="Voltar para seleção de método"
                  className={cn(
                    'text-[15px] text-slate-500 hover:text-slate-700 flex items-center gap-2 transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer rounded-[6px] px-1 py-0.5 active:scale-[0.97]',
                    glowFocus,
                  )}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </button>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-sm text-slate-600">Plano</span>
                  </div>
                  <div className="w-12 h-px bg-slate-300" />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">2</span>
                    </div>
                    <span className="text-sm text-slate-900 font-medium">Pagamento</span>
                  </div>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid lg:grid-cols-5 gap-8">
                {/* Left Column - Plan Summary */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Plan Card */}
                  <div className="relative rounded-[16px] border border-slate-200/80 bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]">
                    {/* Accent line */}
                    <div className="h-1 bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900" />

                    <div className="p-6">
                      {/* Plan name + price row */}
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC] border border-black/[0.12] rounded-[10px] flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                            <div
                              className="absolute inset-0 opacity-[0.04]"
                              style={{
                                backgroundImage:
                                  'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                              }}
                            />
                            <span
                              className="relative z-10 text-[26px] font-black text-transparent bg-clip-text bg-gradient-to-br from-teal-700 to-teal-900 leading-none tracking-tighter"
                              style={{
                                fontFamily: "'Playfair Display', Georgia, serif",
                                fontStyle: 'italic',
                              }}
                            >
                              {planName.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-[15px] font-semibold text-slate-900 leading-tight">
                              {planName}
                            </h3>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {cycleTitleLower}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-baseline">
                            <span className="text-xs text-slate-400 mr-0.5">R$</span>
                            <span className="text-2xl font-bold text-slate-900 tracking-tight">
                              {formatMoney(planPrice)}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400">
                            {cycleSuffix}
                          </p>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="h-px bg-slate-100 mb-4" />

                      {/* Features */}
                      <ul className="space-y-2">
                        {planHighlights.map((feature, i) => (
                          <li
                            key={i}
                            className="flex items-center gap-2.5 text-[13px] text-slate-500"
                          >
                            <div className="w-4 h-4 rounded-full border border-slate-200 flex items-center justify-center flex-shrink-0">
                              <Check className="w-2.5 h-2.5 text-slate-900" strokeWidth={2.5} />
                            </div>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="bg-white rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] border border-slate-100 p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-[10px] bg-slate-50 border border-slate-100 flex items-center justify-center">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#64748b"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <path d="M9 12l2 2 4-4" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Pagamento Seguro</p>
                        <p className="text-xs text-slate-400">Dados criptografados com SSL</p>
                      </div>
                    </div>
                    <div className="w-full h-px bg-slate-100" />
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-[10px] bg-slate-50 border border-slate-100 flex items-center justify-center">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#64748b"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M8 12h8" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Cancele quando quiser</p>
                        <p className="text-xs text-slate-400">Sem multa ou fidelidade</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Payment Form */}
                <div className="lg:col-span-3">
                  <div className="bg-white rounded-[16px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-slate-200 p-8">
                    {/* Form Header */}
                    <div className="flex items-center gap-3 mb-6">
                      <CreditCard className="w-6 h-6 text-indigo-600" />
                      <h2 className="text-xl font-semibold text-slate-900">Dados do cartão</h2>
                    </div>
                    <div className="flex items-center gap-2 mb-8">
                      <MasterCardIcon size={32} />
                      <VisaIcon size={32} />
                      <EloIcon size={32} />
                    </div>

                    {/* Form Fields */}
                    <div className="space-y-5">
                      {/* Nome no cartão */}
                      <div>
                        <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                          Nome no cartão
                        </label>
                        <input
                          type="text"
                          value={cardName}
                          onChange={(e) => setCardName(e.target.value)}
                          placeholder="Luke Skywalker"
                          className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]"
                        />
                      </div>

                      {/* Número do cartão */}
                      <div>
                        <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                          Número do cartão
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                          placeholder="1234 5678 9012 3456"
                          className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                        />
                      </div>

                      {/* Validade e CVV em row */}
                      <div className="flex gap-4">
                        {/* Validade */}
                        <div className="flex-1">
                          <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                            Validade
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={cardExpiry}
                            onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                            placeholder="MM/AA"
                            className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] text-center font-mono tracking-wider"
                          />
                        </div>

                        {/* CVV */}
                        <div className="flex-1">
                          <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                            CVV
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={cardCVV}
                            onChange={(e) => setCardCVV(formatCVV(e.target.value))}
                            placeholder="123"
                            className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] text-center font-mono tracking-wider"
                          />
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-black/[0.06] pt-4 mt-2">
                        <p className="text-[11px] text-black/40 uppercase tracking-wider font-medium mb-3">
                          Dados do titular
                        </p>
                      </div>

                      {/* CPF */}
                      <div>
                        <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                          CPF ou CNPJ
                        </label>
                        <input
                          type="text"
                          inputMode="text"
                          autoCapitalize="characters"
                          value={holderCPF}
                          onChange={(e) => setHolderCPF(maskCpfCnpjAuto(e.target.value))}
                          placeholder="000.000.000-00"
                          className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                        />
                      </div>

                      {/* Telefone */}
                      <div>
                        <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                          Telefone
                        </label>
                        <input
                          type="text"
                          inputMode="tel"
                          value={holderPhone}
                          onChange={(e) => setHolderPhone(formatPhone(e.target.value))}
                          placeholder="(11) 99999-9999"
                          className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                        />
                      </div>

                      {/* CEP e Número em row */}
                      <div className="flex gap-4">
                        {/* CEP */}
                        <div className="flex-[2]">
                          <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                            CEP
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={holderPostalCode}
                            onChange={(e) => setHolderPostalCode(formatPostalCode(e.target.value))}
                            placeholder="00000-000"
                            className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                          />
                        </div>

                        {/* Número */}
                        <div className="flex-1">
                          <label className="block text-[13px] text-black/50 lg:text-slate-500 uppercase tracking-wider font-medium mb-2">
                            Nº
                          </label>
                          <input
                            type="text"
                            value={holderAddressNumber}
                            onChange={(e) => setHolderAddressNumber(e.target.value)}
                            placeholder="123"
                            className="w-full h-[50px] px-4 bg-black/[0.04] lg:bg-slate-100 rounded-[10px] text-[17px] lg:text-[16px] text-black lg:text-slate-900 placeholder:text-black/30 lg:placeholder:text-slate-400 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] text-center"
                          />
                        </div>
                      </div>
                    </div>

                    {/* CTA - Desktop only */}
                    <button
                      onClick={goToReview}
                      aria-label="Continuar para revisão do pedido"
                      className={cn(
                        'w-full h-14 mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-[10px] font-semibold text-[16px] flex items-center justify-center gap-2 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97] cursor-pointer',
                        glowFocus,
                      )}
                    >
                      Continuar
                      <ChevronRight className="w-5 h-5" strokeWidth={2} />
                    </button>

                    {/* Segurança */}
                    <div className="flex items-center justify-center gap-2 mt-4 text-[13px] text-slate-400">
                      <AsaasIcon size={14} aria-label="" />
                      Pagamento seguro via Asaas
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ========== MOBILE LAYOUT ========== */}
            <div className="lg:hidden">
              {/* Card do Plano - Mobile */}
              <div className="relative rounded-[16px] border border-slate-200/80 bg-white overflow-hidden mb-6 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="h-0.5 bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900" />
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gradient-to-br from-[#F5F1EC] to-[#EBE5DC] border border-black/[0.12] rounded-[10px] flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                      <div
                        className="absolute inset-0 opacity-[0.04]"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 1px, transparent 8px)',
                        }}
                      />
                      <span
                        className="relative z-10 text-[22px] font-black text-transparent bg-clip-text bg-gradient-to-br from-teal-700 to-teal-900 leading-none tracking-tighter"
                        style={{
                          fontFamily: "'Playfair Display', Georgia, serif",
                          fontStyle: 'italic',
                        }}
                      >
                        {planName.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-[15px] font-semibold text-slate-900 leading-tight">
                        {planName}
                      </p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {cycleTitleLower}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-baseline">
                      <span className="text-[11px] text-slate-400 mr-0.5">R$</span>
                      <span className="text-xl font-bold text-slate-900 tracking-tight">
                        {formatMoney(planPrice)}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      {cycleSuffix}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Mobile */}
              <div className="space-y-5">
                {/* Nome no cartão */}
                <div>
                  <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                    Nome no cartão
                  </label>
                  <input
                    type="text"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    placeholder="Luke Skywalker"
                    className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]"
                  />
                </div>

                {/* Número do cartão */}
                <div>
                  <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                    Número do cartão
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                  />
                </div>

                {/* Validade e CVV */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                      Validade
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                      placeholder="MM/AA"
                      className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] text-center font-mono tracking-wider"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                      CVV
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={cardCVV}
                      onChange={(e) => setCardCVV(formatCVV(e.target.value))}
                      placeholder="123"
                      className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] text-center font-mono tracking-wider"
                    />
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-black/[0.06] pt-4 mt-2">
                  <p className="text-[11px] text-black/40 uppercase tracking-wider font-medium mb-3">
                    Dados do titular
                  </p>
                </div>

                {/* CPF */}
                <div>
                  <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                    CPF ou CNPJ
                  </label>
                  <input
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    value={holderCPF}
                    onChange={(e) => setHolderCPF(maskCpfCnpjAuto(e.target.value))}
                    placeholder="000.000.000-00"
                    className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                  />
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                    Telefone
                  </label>
                  <input
                    type="text"
                    inputMode="tel"
                    value={holderPhone}
                    onChange={(e) => setHolderPhone(formatPhone(e.target.value))}
                    placeholder="(11) 99999-9999"
                    className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                  />
                </div>

                {/* CEP e Número */}
                <div className="flex gap-4">
                  <div className="flex-[2]">
                    <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                      CEP
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={holderPostalCode}
                      onChange={(e) => setHolderPostalCode(formatPostalCode(e.target.value))}
                      placeholder="00000-000"
                      className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] font-mono tracking-wider"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[13px] text-black/50 uppercase tracking-wider font-medium mb-2">
                      Nº
                    </label>
                    <input
                      type="text"
                      value={holderAddressNumber}
                      onChange={(e) => setHolderAddressNumber(e.target.value)}
                      placeholder="123"
                      className="w-full h-[50px] px-4 bg-black/[0.04] rounded-[10px] text-[17px] text-black placeholder:text-black/30 focus:outline-none focus-visible:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-shadow duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] text-center"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP: Revisão */}
        {step === 'review' && (
          <motion.div
            key="review"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 px-4 pt-4 pb-28 lg:pb-0 lg:pt-0 lg:px-0 lg:flex"
          >
            {/* ========== SIDEBAR ESQUERDA - Branding (Desktop) ========== */}
            <div className="hidden lg:flex lg:w-[45%] lg:bg-gradient-to-br lg:from-[#0F172A] lg:to-[#1E293B] lg:flex-col lg:justify-between lg:p-12 lg:min-h-screen">
              {/* Logo e Header */}
              <div>
                <div className="flex items-center gap-3 mb-12">
                  <div className="w-10 h-10 bg-white rounded-[10px] flex items-center justify-center shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    <img src="/logo-opus-ico.png" alt="" className="w-7 h-7 rounded-full" />
                  </div>
                  <span className="text-white text-xl font-semibold">{saasName ?? 'Opus Owl'}</span>
                </div>

                {/* Headline */}
                <h1 className="text-white text-4xl font-bold leading-tight mb-4">
                  Quase lá!
                  <br />
                  <span className="text-[#22C55E]">Confirme</span> seu pedido
                </h1>
                <p className="text-white/60 text-lg leading-relaxed mb-12">
                  Revise os detalhes do seu plano e finalize a assinatura para começar a usar todos
                  os recursos.
                </p>

                {/* Resumo do Plano */}
                <div className="bg-white/5 rounded-[16px] p-6 border border-white/10">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Seu plano</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white text-2xl font-bold">{planName}</span>
                    <span className="text-[#22C55E] text-2xl font-bold">
                      R$ {formatMoney(planPrice)}
                      {cycleSuffix}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {planHighlights.slice(0, 3).map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#22C55E]" strokeWidth={2.5} />
                        <span className="text-white/70 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                Ambiente 100% seguro • Dados criptografados
              </div>
            </div>

            {/* ========== ÁREA CENTRAL - Review ========== */}
            <div className="lg:flex-1 lg:bg-[#F8FAFC] lg:flex lg:items-center lg:justify-center lg:p-8">
              <div className="lg:w-full lg:max-w-[480px]">
                {/* Header Desktop */}
                <div className="hidden lg:flex lg:items-center lg:justify-between lg:mb-8">
                  <button
                    onClick={() => setStep('payment')}
                    aria-label="Voltar para edição do cartão"
                    className={cn(
                      'text-[15px] text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer rounded-[6px] px-1 py-0.5 active:scale-[0.97]',
                      glowFocus,
                    )}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar
                  </button>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <div className="w-6 h-6 rounded-full bg-[#22C55E] flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                    <span>Pagamento</span>
                    <div className="w-8 h-px bg-slate-300" />
                    <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">3</span>
                    </div>
                    <span className="text-slate-900 font-medium">Revisão</span>
                  </div>
                </div>

                {/* Header Mobile */}
                <div className="text-center mb-6 lg:hidden">
                  <h2 className="text-[22px] font-semibold text-black mb-2">Revisar pedido</h2>
                  <p className="text-[15px] text-black/50">
                    Confira os detalhes antes de finalizar
                  </p>
                </div>

                {/* Card Principal Desktop */}
                <div className="lg:bg-white lg:rounded-[16px] lg:shadow-[0_1px_2px_rgba(0,0,0,0.05)] lg:hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] lg:transition-shadow lg:duration-200 lg:border lg:border-slate-200 lg:overflow-hidden">
                  {/* Card de Resumo */}
                  <div className="bg-white border border-black/[0.08] lg:border-0 rounded-[16px] lg:rounded-none overflow-hidden mb-6 lg:mb-0">
                    {/* Plano */}
                    <div className="p-5 border-b border-black/[0.06] lg:border-slate-100">
                      <p className="text-[11px] text-black/40 lg:text-slate-400 uppercase tracking-widest font-medium mb-1">
                        Plano
                      </p>
                      <div className="flex justify-between items-center">
                        <p className="text-[18px] font-semibold text-black lg:text-slate-900">
                          {planName}
                        </p>
                        <p className="text-[18px] font-bold text-black lg:text-slate-900">
                          R$ {formatMoney(planPrice)}
                          {cycleSuffix}
                        </p>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="p-5 border-b border-black/[0.06] lg:border-slate-100 bg-black/[0.02] lg:bg-slate-50">
                      <p className="text-[11px] text-black/40 lg:text-slate-400 uppercase tracking-widest font-medium mb-3">
                        Incluso no plano
                      </p>
                      <div className="space-y-2">
                        {planHighlights.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-[#22C55E]" strokeWidth={2.5} />
                            <span className="text-[14px] text-black/70 lg:text-slate-600">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Método de Pagamento */}
                    <div className="p-5">
                      <p className="text-[11px] text-black/40 lg:text-slate-400 uppercase tracking-widest font-medium mb-2">
                        Forma de pagamento
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-[10px] bg-[#6366F1]/10 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-[#6366F1]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-[15px] font-medium text-black lg:text-slate-900">
                            {getMaskedCardNumber(cardNumber) || 'Cartão de crédito'}
                          </p>
                          <p className="text-[13px] text-black/50 lg:text-slate-500">
                            {cardExpiry || 'Crédito'}
                          </p>
                        </div>
                        <button
                          onClick={() => setStep('payment')}
                          aria-label="Alterar dados do cartão"
                          className={cn(
                            'ml-auto text-[14px] text-[#007AFF] lg:text-indigo-600 font-medium hover:underline cursor-pointer transition-colors duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] rounded-[6px] px-1 py-0.5 active:scale-[0.97]',
                            glowFocus,
                          )}
                        >
                          Alterar
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Total - Desktop dentro do card */}
                  <div className="hidden lg:flex justify-between items-center p-5 border-t border-slate-200 bg-slate-50">
                    <span className="text-[15px] text-slate-500">Total hoje</span>
                    <span className="text-[24px] font-bold text-slate-900">
                      R$ {formatMoney(effectivePrice)}
                    </span>
                  </div>
                </div>

                {/* Total - Mobile fora do card */}
                <div className="flex lg:hidden justify-between items-center py-4 border-t border-black/[0.08] mb-6">
                  <span className="text-[15px] text-black/60">Total hoje</span>
                  <span className="text-[22px] font-bold text-black">
                    R$ {formatMoney(effectivePrice)}
                  </span>
                </div>

                {/* R1/LGPD — aceite + aviso de recorrência (comum aos layouts) */}
                <ConsentBlock
                  checked={termsAccepted}
                  onChange={setTermsAccepted}
                  priceLabel={`R$ ${formatMoney(effectivePrice)}`}
                  oneTime={isOneTime}
                />

                {/* CTA Desktop */}
                <button
                  onClick={() => void handlePayment()}
                  disabled={isProcessing || !termsAccepted}
                  aria-label={
                    isProcessing
                      ? 'Processando pagamento'
                      : `Pagar ${formatMoney(effectivePrice)} reais`
                  }
                  aria-busy={isProcessing}
                  className={cn(
                    'hidden lg:flex w-full h-14 mt-6 text-white rounded-[10px] font-semibold text-[16px] items-center justify-center gap-2 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
                    glowFocus,
                    isProcessing
                      ? 'bg-slate-700 cursor-not-allowed shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]'
                      : 'bg-slate-900 hover:bg-slate-800 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] active:scale-[0.97] cursor-pointer',
                  )}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Processando…</span>
                    </>
                  ) : (
                    `Pagar R$ ${formatMoney(effectivePrice)}`
                  )}
                </button>

                {/* Segurança */}
                <div className="hidden lg:flex items-center justify-center gap-2 mt-4 text-[13px] text-slate-400">
                  <AsaasIcon size={14} aria-label="" />
                  Pagamento seguro via Asaas
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* STEP: Processando */}
        {step === 'processing' && (
          <motion.div
            key="processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col lg:flex-row"
          >
            {/* Sidebar Desktop */}
            <div className="hidden lg:flex lg:w-[45%] lg:bg-gradient-to-br lg:from-[#0F172A] lg:to-[#1E293B] lg:flex-col lg:justify-center lg:items-center lg:p-12 lg:min-h-screen">
              <Loader2 className="w-16 h-16 text-white animate-spin" />
            </div>

            {/* Área Central */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 lg:bg-[#F8FAFC]">
              <Loader2 className="w-12 h-12 text-black lg:text-slate-900 animate-spin mb-4 lg:hidden" />
              <div className="hidden lg:block lg:bg-white lg:rounded-[16px] lg:shadow-[0_1px_2px_rgba(0,0,0,0.05)] lg:border lg:border-slate-200 lg:p-12 lg:text-center">
                <Loader2 className="w-12 h-12 text-slate-900 animate-spin mx-auto mb-4" />
                <p className="text-[17px] font-medium text-slate-900">Processando pagamento...</p>
                <p className="text-[15px] text-slate-500 mt-1">Aguarde um momento</p>
              </div>
              <p className="text-[17px] font-medium text-black lg:hidden">
                Processando pagamento...
              </p>
              <p className="text-[15px] text-black/50 mt-1 lg:hidden">Aguarde um momento</p>
            </div>
          </motion.div>
        )}

        {/* STEP: Sucesso — Editorial Linoge */}
        {step === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="flex-1 flex items-center justify-center px-4 py-12 lg:py-20 relative overflow-hidden min-h-screen bg-white lg:bg-white"
          >
            {/* Brushstroke decoration top — animado */}
            <motion.div
              initial={{ clipPath: 'inset(0 100% 0 0)' }}
              animate={{ clipPath: 'inset(0 0% 0 0)' }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="absolute top-0 left-0 right-0 h-[180px] lg:h-[260px] pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(ellipse 60% 40% at 30% 50%, rgba(20,184,166,0.12), transparent 60%), radial-gradient(ellipse 50% 30% at 75% 60%, rgba(34,197,94,0.1), transparent 60%)',
              }}
            />

            {/* Linhas decorativas verticais */}
            <motion.div
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="absolute top-0 bottom-0 left-[8%] w-px bg-gradient-to-b from-transparent via-teal-200 to-transparent origin-top hidden lg:block"
            />
            <motion.div
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="absolute top-0 bottom-0 right-[8%] w-px bg-gradient-to-b from-transparent via-teal-200 to-transparent origin-top hidden lg:block"
            />

            <div className="relative w-full max-w-3xl flex flex-col items-center text-center">
              {/* Tag PLANO ATIVO */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex items-center gap-3 mb-6"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-teal-600">
                  Plano ativo
                </span>
                <span className="w-12 h-px bg-teal-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400">
                  {planName}
                </span>
              </motion.div>

              {/* Checkmark animado com pulso */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 200,
                  damping: 15,
                  delay: 0.6,
                }}
                className="relative mb-8"
              >
                {/* Halo pulse */}
                <motion.div
                  animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute inset-0 rounded-full bg-teal-400/30"
                />
                <motion.div
                  animate={{ scale: [1, 1.6, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.6,
                  }}
                  className="absolute inset-0 rounded-full bg-teal-400/20"
                />
                <div className="relative w-24 h-24 lg:w-28 lg:h-28 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-[0_20px_60px_-15px_rgba(20,184,166,0.6)]">
                  <motion.div
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ delay: 1, duration: 0.8, ease: 'easeOut' }}
                  >
                    <Check className="w-12 h-12 lg:w-14 lg:h-14 text-white" strokeWidth={3} />
                  </motion.div>
                </div>
              </motion.div>

              {/* Título Playfair italic gigante */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                className="text-[64px] sm:text-[88px] lg:text-[120px] font-black leading-[0.85] text-slate-900 mb-4"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: 'italic',
                  letterSpacing: '-0.04em',
                }}
              >
                Parabéns
              </motion.h1>

              {/* Linha + subtitle */}
              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ delay: 1.1, duration: 0.6 }}
                className="w-24 h-px bg-teal-500 mb-6 origin-center"
              />

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="text-[14px] sm:text-[15px] uppercase tracking-[0.18em] text-slate-500 mb-2 font-medium"
              >
                Sua assinatura está
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3, duration: 0.6 }}
                className="text-[28px] sm:text-[34px] font-black text-slate-900 mb-12"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: 'italic',
                  letterSpacing: '-0.02em',
                }}
              >
                ativa<span className="text-teal-500">.</span>
              </motion.p>

              {/* Detalhes em cards minimalistas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.7 }}
                className="grid grid-cols-3 gap-4 sm:gap-8 mb-12 w-full max-w-md"
              >
                {[
                  { num: '01', label: 'Acesso', value: 'Imediato' },
                  { num: '02', label: 'Suporte', value: 'Incluso' },
                  { num: '03', label: 'Cancele', value: 'Quando quiser' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5 + i * 0.1, duration: 0.5 }}
                    className="flex flex-col items-center"
                  >
                    <span className="text-[10px] font-bold tabular-nums text-teal-500 mb-1">
                      {item.num}
                    </span>
                    <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1">
                      {item.label}
                    </span>
                    <span className="text-[11px] sm:text-[13px] font-bold text-slate-700">
                      {item.value}
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA Acessar — gradient teal com shimmer (volta pro SaaS de origem) */}
              {successRedirectTarget && (
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.8, duration: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    window.location.href = successRedirectTarget;
                  }}
                  aria-label={saasName ? `Acessar ${saasName}` : 'Acessar painel'}
                  className={cn(
                    'relative overflow-hidden h-14 px-12 rounded-[10px] font-bold text-[12px] uppercase tracking-[0.2em] bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-700 hover:to-teal-600 text-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:shadow-[0_0_0_4px_hsla(187,55%,42%,0.2)] transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer group/btn',
                    glowFocus,
                  )}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)]" />
                  <span className="relative z-10 flex items-center gap-3">
                    {saasName ? `Acessar ${saasName}` : 'Acessar Painel'}
                    <ChevronRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1 duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]" />
                  </span>
                </motion.button>
              )}

              {/* Footer info */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 0.6 }}
                className="mt-8 text-[10px] uppercase tracking-[0.15em] text-slate-400"
              >
                Recibo enviado por e-mail
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom CTA - Review Mobile */}
      {step === 'review' && (
        <div className="fixed bottom-0 left-0 right-0 px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 bg-white border-t border-black/[0.06] lg:hidden">
          <button
            onClick={() => void handlePayment()}
            disabled={isProcessing || !termsAccepted}
            aria-label={
              isProcessing ? 'Processando pagamento' : `Pagar ${formatMoney(effectivePrice)} reais`
            }
            aria-busy={isProcessing}
            className={cn(
              'w-full h-[50px] text-white rounded-[10px] font-semibold text-[17px] flex items-center justify-center gap-2 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)]',
              glowFocus,
              isProcessing
                ? 'bg-black/70 cursor-not-allowed shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]'
                : 'bg-black active:bg-black/90 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.97] cursor-pointer',
            )}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processando…</span>
              </>
            ) : (
              `Pagar R$ ${formatMoney(effectivePrice)}`
            )}
          </button>
        </div>
      )}

      {/* Bottom CTA - Payment Mobile */}
      {step === 'payment' && (
        <div className="fixed bottom-0 left-0 right-0 px-4 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4 bg-white lg:hidden">
          <button
            onClick={goToReview}
            aria-label="Continuar para revisão"
            className={cn(
              'w-full h-[50px] bg-black text-white rounded-[10px] font-semibold text-[17px] flex items-center justify-center gap-2 active:bg-black/90 transition-all duration-200 ease-[cubic-bezier(0.19,1,0.22,1)] cursor-pointer shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.97]',
              glowFocus,
            )}
          >
            Continuar
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Modal de billing info — coleta CPF/telefone antes do PIX (público) */}
      <BillingInfoModal
        open={billingInfoModalOpen}
        onOpenChange={setBillingInfoModalOpen}
        onSaved={handleBillingInfoSaved}
        initialCpfCnpj={buyerDoc}
        initialPhone={buyerPhone}
      />
    </div>
  );
}
